import React, { useState, useEffect, useRef } from 'react';
import { formatOrderPhoneInput } from '../utils/orderPhoneInput';
import useOrderEditProgress from '../hooks/useOrderEditProgress';
import { editProgressPolicy } from '../utils/orderEditProgress';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  FiCalendar,
  FiTrash2,
  FiMapPin,
  FiCheckCircle,
  FiBox,
  FiShield,
  FiPhone,
  FiPlusCircle,
  FiArrowRight,
  FiCheck,
  FiTruck,
  FiZap,
} from 'react-icons/fi';
import { LuClock3 } from 'react-icons/lu';
import { MdElectricScooter, MdMyLocation, MdTwoWheeler } from 'react-icons/md';
import Input from '../components/Input';
import { IoIosClose } from 'react-icons/io';
import Button from '../components/Button';
import SecurityFeeInfoButton from '../components/SecurityFeeInfoButton';
import NearbyPlacesAutocomplete from '../components/NearbyPlacesAutocomplete';
import ServiceabilityModal from '../components/ServiceabilityModal';
import API_URL from "../config/config";
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { ref, set } from 'firebase/database';
import { db } from '../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ORDER_WEIGHT_OPTIONS,
  addMinutesToTimeWithinDay,
  calculateSecurityFeeAmount,
  compareTimeValues,
  getLatestStartTimeForDuration,
  getDistancePrice as getDistancePriceForDistance,
  getWeightCharge,
  getWalletDueComponent,
  normalizeLocationForPayload,
  normalizeLocationsForPayload,
  normalizePriceManipulation,
  toTimeInputValue,
} from '../utils/orderFlow';
import {
  fetchScheduledOrderMinimumTimes,
  PICKUP_SCHEDULE_MINUTES,
} from '../utils/scheduledOrderTime';
import {
  getCachedPriceConfiguration,
  preloadPriceConfiguration,
} from '../utils/priceConfigurationCache';
import { getPreciseCurrentLocation } from '../utils/getPreciseCurrentLocation';
import { checkAddressServiceability } from '../utils/addressServiceability';
import { getCurrentLocationAddressFromGeocodeResults } from '../utils/addressFormatting';
import {
  areLocationsSame,
  hasDuplicateLocations,
  validateDifferentPickupAndDrop,
  validateUniqueLocations,
} from '../utils/locationValidation';
import { dispatchWebsiteNotification } from '../utils/browserNotifications';

const DUPLICATE_ADDRESS_SIDE_MESSAGE =
  'This address is already selected in another stop. Please choose a different one.';
const DUPLICATE_ADDRESS_SNACKBAR_MESSAGE = "Can't use same address.";
const padDatePart = (value) => String(value).padStart(2, "0");
const toTimeInputValueFromDate = (date) =>
  `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
const toDateInputValue = (date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate()
  )}`;
const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};
const QUICK_PACKAGE_OPTIONS = [
  "Documents",
  "Flowers",
  "Food",
  "Clothes",
  "Medicines",
  "Groceries",
  "Cake",
  "Parcel",
];
const DELIVERY_TYPE_FEATURES = {
  now: ["Fast pickup", "Instant dispatch"],
  flexible: ["Pickup in 2.5 hrs", "Delivery in 4-5 hrs"],
  scheduled: ["Pickup on time", "Flexible window"],
};
const normalizePhoneForChangeCheck = (value) => {
  const rawValue = String(value || '');
  let digits = rawValue.replace(/\D/g, '');

  if (rawValue.includes('+91') && digits.startsWith('91')) {
    digits = digits.slice(2);
  } else if (digits.startsWith('91') && digits.length > 10) {
    digits = digits.slice(2);
  }

  return digits.length > 10 ? digits.slice(-10) : digits;
};

const buildLocationChangeSnapshot = (location = {}) => ({
  address: String(location.address || location.text || '').trim(),
  latitude: Number(location.latitude) || 0,
  longitude: Number(location.longitude) || 0,
  buildingAndFlat: String(location.building_and_flat || '').trim(),
  floorAndWing: String(location.floor_and_wing || '').trim(),
  instructions: String(location.instructions || '').trim(),
  name: String(location.name || '').trim(),
  phoneNumber: normalizePhoneForChangeCheck(location.phone_number),
  date: String(location.date || ''),
  time: toTimeInputValue(location.time || location.fromtime || ''),
  fromTime: toTimeInputValue(location.fromtime || location.time || ''),
  toTime: toTimeInputValue(location.totime || ''),
});

const buildOrderChangeSnapshot = ({ formValues, locations }) =>
  JSON.stringify({
    parcelWeight: String(formValues.parcel_weight || ''),
    phoneNumber: normalizePhoneForChangeCheck(formValues.phone_number),
    package: String(formValues.package || '').trim(),
    vehicle: String(formValues.vehicle || ''),
    locations: locations.map(buildLocationChangeSnapshot),
  });

const EditOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [paymentAddress, setPaymentAddress] = useState(null);
  const weight = ORDER_WEIGHT_OPTIONS;
  
  const addressInitialState = {
    text: '',
    place_id: '',
    latitude: 0.0,
    longitude: 0.0,
    building_and_flat: '',
    floor_and_wing: '',
    key: '',
    instructions: '',
    phone_number: '',
    address: '',
    name: '',
    date: '',
    time: '',
    fromtime: '',
    totime: '',
  };

  const initialFormState = {
    delivery_type: 'now',
    parcel_weight: weight[0],
    phone_number: '',
    payment_method: 'cod',
    package: '',
    parcel_value: '',
    vehicle: 'scooty',
  };

  const [pickup, setPickup] = useState(addressInitialState);
  const [drop, setDrop] = useState(addressInitialState);
  const [droplocations, setDroplocations] = useState([]);
  const [formState, setFormState] = useState(initialFormState);
  const [priceData, setPriceData] = useState(() => getCachedPriceConfiguration());
  const [payment, setPayment] = useState('cod');
  const [amount, setAmount] = useState(0.0);
  const [oldAmount, setOldAmount] = useState(0.0);
  const [show, setShow] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPreparingUpdate, setIsPreparingUpdate] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const [showServiceabilityModal, setShowServiceabilityModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [scheduledSegmentMinutes, setScheduledSegmentMinutes] = useState([]);
  const [currentTimeTick, setCurrentTimeTick] = useState(Date.now());
  const initialOrderSnapshotRef = useRef(null);
  const normalizeScheduledPickupAfterDateEditRef = useRef(false);
  const [pricingPreview, setPricingPreview] = useState({
    routeCharge: 0,
    weightCharge: 0,
    flexibleDiscount: 0,
    securityFee: 0,
    walletDue: 0,
    totalDistance: 0,
    total: 0,
  });

  // Match the customer app's edit boundary. The backend records this exact
  // arrival status when the rider reaches pickup; the parcel details remain
  // locked for all later delivery stages as well.
  const editProgress = useOrderEditProgress(order);
  const isParcelDetailsLocked = editProgress.parcelLocked;
  const submissionRevision = useRef(null);
  const canContinueEdit = () => {
    if (!editProgress.alive.current) return false;
    const policy = editProgressPolicy(editProgress.current.current);
    if (policy.blocked || (submissionRevision.current !== null &&
        submissionRevision.current !== editProgress.revision.current)) {
      toast.info(policy.blocked ? policy.message : 'Pickup status changed. Please review the order and try again.');
      setShow(false);
      setIsUpdating(false);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!isParcelDetailsLocked || !order) return;
    // Restore only newly locked parcel fields, not the customer's route draft.
    setFormState((previous) => ({ ...previous,
      parcel_weight: order.parcel_weight, package: order.package || '',
    }));
    setShow(false);
  }, [isParcelDetailsLocked, order]);

  const apiUrl = API_URL;
  const pickupDateMin = toDateInputValue(new Date());
  const pickupDateMax = toDateInputValue(addDays(new Date(), 3));

  const hasOrderChanges = () => {
    if (!initialOrderSnapshotRef.current) {
      return false;
    }

    return (
      buildOrderChangeSnapshot({
        formValues: formState,
        locations: [pickup, drop, ...droplocations],
      }) !== initialOrderSnapshotRef.current
    );
  };

  const noChangesMessage = () =>
    String(order?.delivery_type || '').toLowerCase() === 'scheduled'
      ? 'Please update something in the order'
      : 'Please update anything in the order';

  const dropRouteSignature = droplocations
    .map(
      (location) =>
        `${location?.key || ''}:${Number(location?.latitude) || 0},${
          Number(location?.longitude) || 0
        }`
    )
    .join('|');
  const hasPendingDropPoints = droplocations.some(
    (location) => !location?.latitude || !location?.longitude
  );
  const hasDuplicateRouteSelection = hasDuplicateLocations([pickup, drop, ...droplocations]);
  const shouldShowFareBox =
    !hasPendingDropPoints &&
    !hasDuplicateRouteSelection &&
    (Number(amount || 0) > 0 || Number(oldAmount || 0) > 0);
  const amountDifference = Number(amount || 0) - Number(oldAmount || 0);
  const routeAndWeightAmount =
    Number(pricingPreview.routeCharge || 0) +
    Number(pricingPreview.weightCharge || 0);
  const orderAmount = Number(pricingPreview.routeCharge || 0);
  const securityFeeAmount = Number(pricingPreview.securityFee || 0);
  const displayedFareAmount =
    pricingPreview.total ?? amount ?? oldAmount ?? 0;
  const configuredFlexibleDiscountPercent = Number(
    priceData?.flexibleDeliveryDiscountPercent ??
      priceData?.flexible_delivery_discount_percent ??
      0
  );
  const flexibleDiscountPercent = Number.isFinite(
    configuredFlexibleDiscountPercent
  )
    ? Math.min(Math.max(configuredFlexibleDiscountPercent, 0), 100)
    : 0;
  const flexibleDiscountLabel = `${
    Number.isInteger(flexibleDiscountPercent)
      ? flexibleDiscountPercent
      : flexibleDiscountPercent.toFixed(2).replace(/\.0+$/, '')
  }% off`;
  const fareStatusMessage = hasPendingDropPoints
    ? 'Complete every added drop point to update fare'
    : priceLoading
      ? 'Updating fare...'
      : 'Updates automatically as you edit';
  const canAutoUpdateFare =
    !loading &&
    Boolean(order?._id) &&
    Boolean(Number(pickup.latitude)) &&
    Boolean(Number(pickup.longitude)) &&
    Boolean(Number(drop.latitude)) &&
    Boolean(Number(drop.longitude)) &&
    !hasPendingDropPoints;
  const scheduledSegmentSignature = (scheduledSegmentMinutes || [])
    .map((value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
    })
    .join('|');

  const syncSharedScheduledDate = (dateValue) => {
    setPickup((current) => ({ ...current, date: dateValue }));
    setDrop((current) => ({ ...current, date: dateValue }));
    setDroplocations((current) =>
      current.map((location) => ({
        ...location,
        date: dateValue,
      }))
    );
  };

  const openServiceabilityModal = () => {
    setShowServiceabilityModal(true);
  };

  const showDuplicateAddressFeedback = () => {
    toast.dismiss('duplicate-address-side');
    toast.dismiss('duplicate-address-snackbar');
    toast.error(DUPLICATE_ADDRESS_SIDE_MESSAGE, {
      position: 'top-right',
      toastId: 'duplicate-address-side',
    });
    toast.error(DUPLICATE_ADDRESS_SNACKBAR_MESSAGE, {
      position: 'bottom-center',
      autoClose: 5000,
      toastId: 'duplicate-address-snackbar',
    });
  };

  const getComparableLocationsForTarget = (targetType, index = null) => {
    if (targetType === 'pickup') {
      return [drop, ...droplocations];
    }

    if (targetType === 'drop') {
      return [pickup, ...droplocations];
    }

    if (targetType === 'droppoint' && index !== null) {
      return [pickup, drop, ...droplocations.filter((_, itemIndex) => itemIndex !== index)];
    }

    return [];
  };

  const hasDuplicateLocationSelection = (candidateLocation, targetType, index = null) =>
    getComparableLocationsForTarget(targetType, index).some((location) =>
      areLocationsSame(candidateLocation, location)
    );

  const resetLocationSearch = (currentLocation, text) => ({
    ...currentLocation,
    text,
    address: text,
    place_id: '',
    latitude: 0,
    longitude: 0,
  });

  const formatPhoneNumber = (input) => {
    const rawInput = String(input || '');
    let numericInput = rawInput.replace(/\D/g, '');

    if (rawInput.includes('+91') && numericInput.startsWith('91')) {
      numericInput = numericInput.slice(2);
    } else if (numericInput.startsWith('91') && numericInput.length > 10) {
      numericInput = numericInput.slice(2);
    }
    if (numericInput.length > 10) {
      numericInput = numericInput.slice(0, 10);
    }

    let formattedNumber = '+91';
    if (numericInput.length > 0) {
      formattedNumber += ' ';
    }
    if (numericInput.length > 5) {
      formattedNumber += `${numericInput.slice(0, 5)} ${numericInput.slice(5, 10)}`;
    } else {
      formattedNumber += numericInput;
    }

    return formattedNumber;
  };

  const getPhoneInputValue = (input) => {
    const rawInput = String(input || '');
    let numericInput = rawInput.replace(/\D/g, '');

    if (rawInput.includes('+91') && numericInput.startsWith('91')) {
      numericInput = numericInput.slice(2);
    } else if (numericInput.startsWith('91') && numericInput.length > 10) {
      numericInput = numericInput.slice(2);
    }
    if (numericInput.length > 10) {
      numericInput = numericInput.slice(0, 10);
    }

    if (numericInput.length > 5) {
      return `${numericInput.slice(0, 5)} ${numericInput.slice(5, 10)}`;
    }
    return numericInput;
  };

  const renderPrefixedPhoneInput = ({
    value,
    onChange,
    label,
    id,
    error,
    placeholder = "Enter your 10 digit mobile number",
    required = true,
  }) => (
    <div className='w-full flex flex-col items-start'>
      <label htmlFor={id} className='pb-1 text-sm lg:text-base'>{label}</label>
      <div className={`flex w-full overflow-hidden rounded-xl border-2 ${error ? 'border-red-500' : 'border-accentYellow'}`}>
        <span className="flex min-w-[72px] items-center justify-center border-r border-[#e5e7eb] bg-[#fff6df] px-3 text-[14px] font-medium text-[#191919]">
          +91
        </span>
        <input
          required={required}
          onChange={(e) => onChange(formatOrderPhoneInput(e.target.value, value))}
          value={getPhoneInputValue(value)}
          className='w-full outline-none bg-white px-4 py-3 text-[14px] text-[#1e2430] placeholder:text-[#8f96aa]'
          type="text"
          placeholder={placeholder}
          id={id}
          autoComplete="tel-national"
          inputMode="numeric"
        />
      </div>
      {error ? <p className='mt-1 text-xs text-red-500'>{error}</p> : null}
    </div>
  );

  const getPhoneDigits = (value) => {
    const rawValue = String(value || "");
    let digits = rawValue.replace(/\D/g, "");

    if (rawValue.includes("+91") && digits.startsWith("91")) {
      digits = digits.slice(2);
    } else if (digits.startsWith("91") && digits.length > 10) {
      digits = digits.slice(2);
    }

    return digits;
  };

  const isValidPhoneNumber = (value) => {
    const digits = getPhoneDigits(value);
    return digits.length === 10;
  };

  const updatePhoneFieldError = (fieldKey, value) => {
    setFormErrors((current) => {
      const nextErrors = { ...current };
      if (isValidPhoneNumber(value)) {
        delete nextErrors[fieldKey];
      } else {
        nextErrors[fieldKey] = "Please enter a valid 10-digit mobile number";
      }
      return nextErrors;
    });
  };

  const clearFieldErrors = (...keys) => {
    const validKeys = keys.filter(Boolean);
    if (!validKeys.length) return;

    setFormErrors((current) => {
      const nextErrors = { ...current };
      validKeys.forEach((key) => {
        delete nextErrors[key];
      });
      return nextErrors;
    });
  };

  const getDropPointErrorKey = (field, index) => `droppoint_${field}_${index}`;

  const preventManualTimeTyping = (event) => {
    event.preventDefault();
  };

  const getSegmentMinimumMinutes = (segmentIndex) => {
    const value = Number(scheduledSegmentMinutes?.[segmentIndex]);
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const getCurrentPickupFromTime = (dateValue = pickup.date || pickupDateMin) => {
    if (!dateValue || dateValue !== pickupDateMin) {
      return '';
    }

    return toTimeInputValueFromDate(new Date(currentTimeTick));
  };

  const getRequiredFromTimeForStop = (type, index = null) => {
    if (type === 'pickup') {
      return getCurrentPickupFromTime();
    }

    if (type === 'drop') {
      return pickup.totime || '';
    }

    if (type === 'droppoint' && index !== null) {
      if (index === 0) {
        return drop.totime || pickup.totime || '';
      }
      return droplocations[index - 1]?.totime || '';
    }

    return '';
  };

  const getPickupInputMinimum = () => {
    const currentTimeMinimum = getRequiredFromTimeForStop('pickup');
    // Existing scheduled orders can legitimately retain their saved slot even
    // after that time has passed. Leaving a native min on that value prevents
    // the form from submitting and focuses the pickup field before our update
    // validation can run. Explicit time edits still use the same check below.
    if (
      pickup.fromtime &&
      currentTimeMinimum &&
      compareTimeValues(pickup.fromtime, currentTimeMinimum) === -1
    ) {
      return '';
    }
    return currentTimeMinimum;
  };

  const getMinimumToTimeForStop = (type, index = null, fromTimeOverride = '') => {
    const fromTime =
      fromTimeOverride ||
      (type === 'pickup'
        ? pickup.fromtime
        : type === 'drop'
          ? drop.fromtime
          : index !== null
            ? droplocations[index]?.fromtime
            : '');

    if (!fromTime) return '';

    const minimumMinutes =
      type === 'pickup'
        ? PICKUP_SCHEDULE_MINUTES
        : type === 'drop'
          ? getSegmentMinimumMinutes(0)
          : index !== null
            ? getSegmentMinimumMinutes(index + 1)
            : null;

    if (!Number.isFinite(Number(minimumMinutes)) || Number(minimumMinutes) <= 0) {
      return '';
    }

    return addMinutesToTimeWithinDay(fromTime, minimumMinutes);
  };

  const getMaximumFromTimeForStop = (type, index = null) => {
    const durationMinutes =
      type === 'pickup'
        ? PICKUP_SCHEDULE_MINUTES
        : type === 'drop'
          ? getSegmentMinimumMinutes(0)
          : index !== null
            ? getSegmentMinimumMinutes(index + 1)
            : null;

    if (!Number.isFinite(Number(durationMinutes)) || Number(durationMinutes) <= 0) {
      return '';
    }

    return getLatestStartTimeForDuration(durationMinutes);
  };

  const getDropTimeErrorMessage = (minimumMinutes) => {
    return Number.isFinite(Number(minimumMinutes)) && Number(minimumMinutes) > 0
      ? `To time must be at least ${Number(minimumMinutes)} minutes after from time`
      : 'Please select a valid time';
  };

  const buildScheduledRouteTimeState = (
    segmentMinutes,
    pickupState = pickup,
    dropState = drop,
    dropLocationsState = droplocations,
    forceRouteWindows = false
  ) => {
    if (formState.delivery_type !== 'scheduled') {
      return null;
    }

    const nextPickup = { ...pickupState };
    const nextDrop = { ...dropState };
    const nextDroplocations = (dropLocationsState || []).map((location) => ({
      ...location,
    }));

    const pickupRequiredFrom = getCurrentPickupFromTime(
      nextPickup.date || pickupDateMin
    );
    if (
      pickupRequiredFrom &&
      !nextPickup.fromtime
    ) {
      nextPickup.fromtime = pickupRequiredFrom;
      nextPickup.time = pickupRequiredFrom;
    }

    const pickupMinimumTo = nextPickup.fromtime
      ? addMinutesToTimeWithinDay(nextPickup.fromtime, PICKUP_SCHEDULE_MINUTES)
      : '';
    if (
      pickupMinimumTo &&
      (forceRouteWindows ||
        !nextPickup.totime ||
        compareTimeValues(nextPickup.totime, pickupMinimumTo) === -1)
    ) {
      nextPickup.totime = pickupMinimumTo;
    }

    const firstRequiredFrom = nextPickup.totime || pickupMinimumTo;
    if (
      firstRequiredFrom &&
      (forceRouteWindows ||
        !nextDrop.fromtime ||
        compareTimeValues(nextDrop.fromtime, firstRequiredFrom) === -1)
    ) {
      nextDrop.fromtime = firstRequiredFrom;
      nextDrop.time = firstRequiredFrom;
    }

    const firstSegmentMinutes = Number(segmentMinutes?.[0]);
    const dropExactToTime =
      nextDrop.fromtime &&
      Number.isFinite(firstSegmentMinutes) &&
      firstSegmentMinutes > 0
        ? addMinutesToTimeWithinDay(nextDrop.fromtime, firstSegmentMinutes)
        : '';
    if (dropExactToTime) {
      nextDrop.totime = dropExactToTime;
    }

    let previousToTime =
      nextDrop.totime || dropExactToTime || nextDrop.fromtime || firstRequiredFrom;

    nextDroplocations.forEach((location, index) => {
      if (
        previousToTime &&
        (forceRouteWindows ||
          !location.fromtime ||
          compareTimeValues(location.fromtime, previousToTime) === -1)
      ) {
        location.fromtime = previousToTime;
        location.time = previousToTime;
      }

      const segmentValue = Number(segmentMinutes?.[index + 1]);
      const exactToTime =
        location.fromtime &&
        Number.isFinite(segmentValue) &&
        segmentValue > 0
          ? addMinutesToTimeWithinDay(location.fromtime, segmentValue)
          : '';

      if (exactToTime) {
        location.totime = exactToTime;
        previousToTime = exactToTime;
      } else {
        previousToTime = location.totime || location.fromtime || previousToTime;
      }
    });

    return {
      pickup: nextPickup,
      drop: nextDrop,
      droplocations: nextDroplocations,
    };
  };

  const syncScheduledRouteTimesFromPickup = (fromtime) => {
    const syncedTimeState = buildScheduledRouteTimeState(
      scheduledSegmentMinutes,
      { ...pickup, fromtime, time: fromtime },
      drop,
      droplocations,
      true
    );

    if (!syncedTimeState) return;
    setPickup(syncedTimeState.pickup);
    setDrop(syncedTimeState.drop);
    setDroplocations(syncedTimeState.droplocations);
  };

  const handlePickupDateChange = (value) => {
    // A loaded order may intentionally have a time earlier than the current
    // clock. Only enforce the current-time boundary after the customer has
    // explicitly moved its scheduled date.
    normalizeScheduledPickupAfterDateEditRef.current = true;
    syncSharedScheduledDate(value);
    clearFieldErrors("pickup_date");
  };

  const resetFareSummary = () => {
    setAmount(0);
    setShowPriceDetails(false);
    setScheduledSegmentMinutes([]);
    setPricingPreview((previous) => ({
      ...previous,
      routeCharge: 0,
      weightCharge: 0,
      securityFee: 0,
      totalDistance: 0,
      total: 0,
    }));
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTimeTick(Date.now());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (formState.delivery_type !== 'scheduled') {
      return;
    }

    const pickupRequiredFrom = getRequiredFromTimeForStop('pickup');
    const shouldNormalizePickupForDateEdit =
      normalizeScheduledPickupAfterDateEditRef.current;
    normalizeScheduledPickupAfterDateEditRef.current = false;
    const pickupMinimumTo = getMinimumToTimeForStop('pickup', null, pickup.fromtime);
    const pickupMaximumFrom = getMaximumFromTimeForStop('pickup');

    setPickup((current) => {
      const nextState = { ...current };

      if (
        pickupRequiredFrom &&
        (!current.fromtime ||
          (shouldNormalizePickupForDateEdit &&
            compareTimeValues(current.fromtime, pickupRequiredFrom) === -1))
      ) {
        nextState.fromtime = pickupRequiredFrom;
      }

      if (pickupMaximumFrom && nextState.fromtime && compareTimeValues(nextState.fromtime, pickupMaximumFrom) === 1) {
        nextState.fromtime = pickupMaximumFrom;
      }

      const recalculatedPickupMinTo = getMinimumToTimeForStop(
        'pickup',
        null,
        nextState.fromtime
      );

      if (
        recalculatedPickupMinTo &&
        (!nextState.totime || compareTimeValues(nextState.totime, recalculatedPickupMinTo) === -1)
      ) {
        nextState.totime = recalculatedPickupMinTo;
        nextState.time = nextState.fromtime;
      }

      return nextState;
    });

    const pickupToTime = pickup.totime || pickupMinimumTo;
    if (pickupToTime) {
      setDrop((current) => {
        const nextState = { ...current };
        if (!nextState.fromtime || compareTimeValues(nextState.fromtime, pickupToTime) === -1) {
          nextState.fromtime = pickupToTime;
          nextState.time = pickupToTime;
        }
        const minimumDropTo = getMinimumToTimeForStop('drop', null, nextState.fromtime);
        if (minimumDropTo && (!nextState.totime || compareTimeValues(nextState.totime, minimumDropTo) === -1)) {
          nextState.totime = minimumDropTo;
        }
        return nextState;
      });
    }

    setDroplocations((current) =>
      current.map((location, index) => {
        const requiredFrom =
          index === 0
            ? drop.totime || getMinimumToTimeForStop('drop')
            : current[index - 1]?.totime || '';
        const nextState = { ...location };
        if (requiredFrom && (!nextState.fromtime || compareTimeValues(nextState.fromtime, requiredFrom) === -1)) {
          nextState.fromtime = requiredFrom;
          nextState.time = requiredFrom;
        }
        const minimumTo = getMinimumToTimeForStop('droppoint', index, nextState.fromtime);
        if (minimumTo && (!nextState.totime || compareTimeValues(nextState.totime, minimumTo) === -1)) {
          nextState.totime = minimumTo;
        }
        return nextState;
      })
    );
  }, [
    formState.delivery_type,
    currentTimeTick,
    pickup.date,
    pickup.fromtime,
    pickup.totime,
    drop.fromtime,
    drop.totime,
    droplocations.map((location) => `${location?.fromtime || ''}|${location?.totime || ''}`).join('||'),
    scheduledSegmentMinutes,
  ]);

  useEffect(() => {
    if (
      formState.delivery_type !== 'scheduled' ||
      !scheduledSegmentSignature ||
      !pickup.totime
    ) {
      return;
    }

    setDrop((current) => {
      const nextState = { ...current };
      let hasChanges = false;
      const requiredFrom = pickup.totime;
      const nextFromTime =
        requiredFrom &&
        (!nextState.fromtime || compareTimeValues(nextState.fromtime, requiredFrom) === -1)
          ? requiredFrom
          : nextState.fromtime;

      if (nextFromTime !== nextState.fromtime) {
        nextState.fromtime = nextFromTime;
        nextState.time = nextFromTime;
        hasChanges = true;
      }

      const exactToTime = getMinimumToTimeForStop('drop', null, nextState.fromtime);
      if (exactToTime && exactToTime !== nextState.totime) {
        nextState.totime = exactToTime;
        hasChanges = true;
      }

      return hasChanges ? nextState : current;
    });

    setDroplocations((current) => {
      let previousToTime = drop.totime || getMinimumToTimeForStop('drop', null, pickup.totime);
      let hasChanges = false;

      const nextLocations = current.map((location, index) => {
        const nextState = { ...location };
        const requiredFrom = previousToTime || '';
        const nextFromTime =
          requiredFrom &&
          (!nextState.fromtime || compareTimeValues(nextState.fromtime, requiredFrom) === -1)
            ? requiredFrom
            : nextState.fromtime;

        if (nextFromTime !== nextState.fromtime) {
          nextState.fromtime = nextFromTime;
          nextState.time = nextFromTime;
          hasChanges = true;
        }

        const exactToTime = getMinimumToTimeForStop('droppoint', index, nextState.fromtime);
        if (exactToTime && exactToTime !== nextState.totime) {
          nextState.totime = exactToTime;
          previousToTime = exactToTime;
          hasChanges = true;
        } else {
          previousToTime = nextState.totime || nextState.fromtime || previousToTime;
        }

        return nextState;
      });

      return hasChanges ? nextLocations : current;
    });
  }, [
    formState.delivery_type,
    pickup.totime,
    drop.totime,
    scheduledSegmentSignature,
  ]);

  const clearLocationSearch = (type, index = null) => {
    if (type === "pickup") {
      setPickup((current) => resetLocationSearch(current, ""));
      clearFieldErrors("pickup_search");
      resetFareSummary();
      return;
    }

    if (type === "drop") {
      setDrop((current) => resetLocationSearch(current, ""));
      clearFieldErrors("drop_search");
      resetFareSummary();
      return;
    }

    if (type === "droppoint" && index !== null) {
      setDroplocations((current) =>
        current.map((item, itemIndex) =>
          itemIndex === index ? resetLocationSearch(item, "") : item
        )
      );
      clearFieldErrors(getDropPointErrorKey("search", index));
      resetFareSummary();
    }
  };

  const handleQuickPackageSelect = (packageType) => {
    if (isParcelDetailsLocked) return;
    setFormState((current) => ({
      ...current,
      package: packageType,
    }));

    if (formErrors.package) {
      clearFieldErrors("package");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const inputs = document.querySelectorAll('#orderForm input');
    inputs.forEach(input => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      });
    });
    return () => {
      inputs.forEach(input => {
        input.removeEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        });
      });
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to your account!');
      navigate('/login');
      return;
    }

    initialOrderSnapshotRef.current = null;

    let isActive = true;
    preloadPriceConfiguration()
      .then((configuration) => {
        if (isActive) {
          setPriceData(configuration);
        }
      })
      .catch((err) => {
        console.error('Error fetching price data:', err);
        toast.error('Failed to load pricing information');
      });

    // Fetch order data
    axios.get(`${apiUrl}/order/customer_app/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        const orderData = res.data.order;
        if (!orderData) {
          throw new Error('No order data returned');
        }
        const hasPricingEditRestriction =
          Number(orderData?.pointsUsed || 0) > 0 ||
          Number(orderData?.coupon_discount_amount || 0) > 0 ||
          String(orderData?.coupon_code || '').trim() !== '';
        if (hasPricingEditRestriction) {
          toast.error('Orders with coupon or points applied cannot be edited. Please cancel this order instead.');
          navigate('/orders', { replace: true });
          return;
        }
        // Build the baseline from exactly the same normalized values assigned
        // to the form below. The API stores several location formats whereas
        // the form normalizes text, phone numbers, and times for display.
        // Comparing that raw response made an untouched order look edited.
        const initialFormValues = {
          delivery_type: orderData.delivery_type || 'now',
          parcel_weight: orderData.parcel_weight || weight[0],
          phone_number: orderData.phone_number
            ? formatPhoneNumber(orderData.phone_number)
            : '',
          payment_method: orderData.payment_method || 'cod',
          package: orderData.package || '',
          parcel_value:
            Number(orderData.parcel_value) > 0
              ? String(orderData.parcel_value)
              : '',
          vehicle: orderData.vehicle || 'scooty',
        };
        const initialLocations = (orderData.locations || []).map((location) => ({
          ...addressInitialState,
          ...location,
          text: location.text || location.address || '',
          time: toTimeInputValue(location.time || location.fromtime || ''),
          fromtime: toTimeInputValue(location.fromtime || location.time || ''),
          totime: toTimeInputValue(location.totime || ''),
          phone_number: location.phone_number
            ? formatPhoneNumber(location.phone_number)
            : '',
          key: location.key || uuidv4(),
        }));
        initialOrderSnapshotRef.current = buildOrderChangeSnapshot({
          formValues: initialFormValues,
          locations: initialLocations,
        });
        setOrder(orderData);
        setOldAmount(orderData.amount || 0);
        setAmount(orderData.amount || 0);
        const walletDueComponent = getWalletDueComponent(orderData);
        setPricingPreview({
          routeCharge: Math.max(
            (orderData.amount || 0) -
              (Number(orderData.security_fee) || 0) -
              walletDueComponent,
            0
          ),
          weightCharge: 0,
          flexibleDiscount: Math.max(
            Number(orderData.flexible_delivery_discount_amount) || 0,
            0
          ),
          securityFee: Number(orderData.security_fee) || 0,
          walletDue: walletDueComponent,
          totalDistance: 0,
          total: orderData.amount || 0,
        });

        // Initialize form state
        setFormState(initialFormValues);

        // Initialize pickup address
        if (orderData.locations && orderData.locations[0]) {
          setPickup(initialLocations[0]);
        }

        // Initialize drop address
        if (orderData.locations && orderData.locations[1]) {
          setDrop(initialLocations[1]);
        }

        // Initialize droplocations
        if (orderData.locations && orderData.locations.length > 2) {
          setDroplocations(initialLocations.slice(2));
        }

        // Set payment address
        setPaymentAddress(orderData.payment_address || null);
        setPayment(orderData.payment_method || 'cod');
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching order:', err);
        toast.error('Failed to load order details: ' + (err.message || 'Unknown error'));
        setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [orderId, navigate]);

  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const normalizeAddressForSubmit = (addr, index) => {
    const existingOrderAddress = order?.locations?.[index] || {};
    const preservedOrderAddressId =
      addr?.order_address_id ||
      existingOrderAddress?.order_address_id ||
      "";
    const stableKey =
      addr?.key ||
      existingOrderAddress?.key ||
      uuidv4();

    return normalizeLocationForPayload({
      text: addr?.text || addr?.address || '',
      place_id: addr?.place_id || addr?.placeId || '',
      latitude: Number(addr?.latitude) || 0,
      longitude: Number(addr?.longitude) || 0,
      building_and_flat: addr?.building_and_flat || '',
      floor_and_wing: addr?.floor_and_wing || '',
      key: stableKey,
      order_address_id: preservedOrderAddressId,
      instructions: addr?.instructions || '',
      phone_number: addr?.phone_number || '',
      address: addr?.address || addr?.text || '',
      name: addr?.name || '',
      date: addr?.date || '',
      time: addr?.time || '',
      fromtime: addr?.fromtime || '',
      totime: addr?.totime || '',
      distance: Number(addr?.distance) || 0,
    }, formState.delivery_type);
  };

  const sanitizeAllLocations = () => {
    const rawLocations = [pickup, drop, ...droplocations];
    return normalizeLocationsForPayload(rawLocations.map((locationItem, idx) =>
      normalizeAddressForSubmit(locationItem, idx)
    ), formState.delivery_type);
  };

  const getGstDetailsPayload = () => {
    if (order?.gstDetails?.includeGst) {
      return {
        includeGst: true,
        legalName: order.gstDetails.legalName || '',
        gstin: order.gstDetails.gstin || '',
      };
    }
    return {
      includeGst: false,
      legalName: '',
      gstin: '',
    };
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser.');
      return;
    }

    setIsFetchingLocation(true);
    getPreciseCurrentLocation({
      desiredAccuracy: 80,
      timeoutMs: 12000,
      maximumAge: 0,
    })
      .then(async (position) => {
        const { latitude, longitude } = position.coords;
        const verification = await checkAddressServiceability({ latitude, longitude });
        if (!verification.serviceable) {
          openServiceabilityModal();
          setIsFetchingLocation(false);
          return;
        }

        const geocoder = window.google?.maps?.Geocoder
          ? new window.google.maps.Geocoder()
          : null;
        if (!geocoder) {
          const nextPickup = {
            ...pickup,
            text: `${latitude}, ${longitude}`,
            address: `${latitude}, ${longitude}`,
            latitude,
            longitude,
            place_id: '',
            key: pickup.key || uuidv4(),
          };
          if (hasDuplicateLocationSelection(nextPickup, 'pickup')) {
            setPickup((current) => resetLocationSearch(current, ''));
            setShowPriceDetails(false);
            showDuplicateAddressFeedback();
            setIsFetchingLocation(false);
            return;
          }
          setPickup(nextPickup);
          clearFieldErrors("pickup_search");
          setIsFetchingLocation(false);
          return;
        }

        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            const address = getCurrentLocationAddressFromGeocodeResults(
              results,
              `${latitude}, ${longitude}`
            );
            const nextPickup = {
              ...pickup,
              text: address,
              address,
              latitude,
              longitude,
              place_id: results[0]?.place_id || '',
              key: pickup.key || uuidv4(),
            };
            if (hasDuplicateLocationSelection(nextPickup, 'pickup')) {
              setPickup((current) => resetLocationSearch(current, ''));
              setShowPriceDetails(false);
              showDuplicateAddressFeedback();
              setIsFetchingLocation(false);
              return;
            }
            setPickup(nextPickup);
            clearFieldErrors("pickup_search");
          } else {
            toast.error('Failed to get address from location');
          }
          setIsFetchingLocation(false);
        });
      })
      .catch((error) => {
        console.error('Geolocation error:', error);
        const errorMessage =
          error.code === error.PERMISSION_DENIED
            ? 'Please allow location access to use your current pickup.'
            : error.code === error.TIMEOUT || /timed out/i.test(error.message || '')
              ? 'Getting your location timed out. Please try again.'
              : 'Failed to get current location. Please try again.';
        toast.error(errorMessage);
        setIsFetchingLocation(false);
      });
  };

  const applySelectedAddress = (currentLocation, selectedLocation) => {
    return {
      ...currentLocation,
      text: selectedLocation.description,
      address: selectedLocation.address || selectedLocation.description,
      latitude: selectedLocation.latitude,
      longitude: selectedLocation.longitude,
      place_id: selectedLocation.place_id || selectedLocation.placeId || '',
      key: currentLocation.key || uuidv4(),
    };
  };

  const handlePickupSelect = (selectedLocation) => {
    const nextPickup = applySelectedAddress(pickup, selectedLocation);
    if (!nextPickup) return false;

    if (hasDuplicateLocationSelection(nextPickup, 'pickup')) {
      clearLocationSearch('pickup');
      showDuplicateAddressFeedback();
      return false;
    }

    setPickup(nextPickup);
    clearFieldErrors('pickup_search');
    return true;
  };

  const handleDropSelect = (selectedLocation) => {
    const nextDrop = applySelectedAddress(drop, selectedLocation);
    if (!nextDrop) return false;

    if (hasDuplicateLocationSelection(nextDrop, 'drop')) {
      clearLocationSearch('drop');
      showDuplicateAddressFeedback();
      return false;
    }

    setDrop(nextDrop);
    clearFieldErrors('drop_search');
    return true;
  };

  const handleDropLocationSelect = (index, selectedLocation) => {
    const nextDropLocation = applySelectedAddress(
      droplocations[index],
      selectedLocation
    );
    if (!nextDropLocation) return false;

    if (hasDuplicateLocationSelection(nextDropLocation, 'droppoint', index)) {
      clearLocationSearch('droppoint', index);
      showDuplicateAddressFeedback();
      return false;
    }

    setDroplocations((current) =>
      current.map((droplocation, locationIndex) =>
        locationIndex === index ? nextDropLocation : droplocation
      )
    );
    clearFieldErrors(getDropPointErrorKey('search', index));
    return true;
  };

  const handleWeightChange = (selectedWeight) => {
    if (isParcelDetailsLocked) return;
    setFormState((current) => ({
      ...current,
      parcel_weight: selectedWeight,
    }));
  };

  const handleDropLocationChange = (index, field, value) => {
    setDroplocations((current) =>
      current.map((location, locationIndex) =>
        locationIndex === index ? { ...location, [field]: value } : location
      )
    );

    if (field === 'phone_number' || field === 'name' || field === 'fromtime' || field === 'totime') {
      clearFieldErrors(getDropPointErrorKey(field, index));
    }
  };

  const fetchDistanceAndCost = async (options = {}) => {
    const { showErrors = true } = options;
    if (
      !pickup.latitude ||
      !pickup.longitude ||
      !drop.latitude ||
      !drop.longitude ||
      hasPendingDropPoints
    ) {
      return null;
    }

    if (!validateDifferentPickupAndDrop(pickup, drop, showErrors ? toast : null)) {
      setAmount(0);
      setPricingPreview((prev) => ({
        ...prev,
        routeCharge: 0,
        weightCharge: 0,
        flexibleDiscount: 0,
        securityFee: 0,
        walletDue: 0,
        totalDistance: 0,
        total: 0,
      }));
      return null;
    }

    if (!validateUniqueLocations([pickup, drop, ...droplocations])) {
      setShowPriceDetails(false);
      return null;
    }

    setPriceLoading(true);
    let distArr = [];
    try {
      const res = await axios.get(`${apiUrl}/price/get`);
      const priceManipulation = normalizePriceManipulation(
        res.data?.priceManipulation
      );
      setPriceData(priceManipulation);

      // Match the edit preview to the server calculation. In particular, a
      // pickup entering a live heatmap zone must not retain the old order's
      // normal-rate preview.
      let quote = null;
      try {
        const token = localStorage.getItem('token');
        const existingOrderId = order?._id || order?.id || orderId;
        if (token && existingOrderId) {
          const quoteResponse = await axios.post(
            `${apiUrl}/order/quote`,
            {
              order_id: existingOrderId,
              locations: sanitizeAllLocations(),
              parcel_weight: formState.parcel_weight,
              parcel_value: Number(formState.parcel_value) || 0,
              delivery_type: formState.delivery_type,
              payment_method: payment,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!quoteResponse.data?.error &&
              Number.isFinite(Number(quoteResponse.data?.amount))) {
            quote = quoteResponse.data;
          }
        }
      } catch (error) {
        console.warn('Authoritative edit quote unavailable:', error);
      }

      if (!quote || !Array.isArray(quote.distances) ||
          quote.distances.length !== droplocations.length + 1 ||
          quote.distances.some(value => !Number.isFinite(value) || value <= 0)) {
        throw new Error('Unable to verify delivery distances.');
      }
      distArr = quote.distances;
      const mainDistance = distArr[0];
      const totalDistance = distArr.reduce((sum, distance) => sum + distance, 0);
      const totalAmount = distArr.reduce((sum, distance, index) => sum +
        distance * getDistancePriceForDistance(distance, priceManipulation) +
        (index > 0 ? priceManipulation.additionalDropCharge || 0 : 0), 0);

      const segmentMinutes =
        formState.delivery_type === 'scheduled'
          ? await fetchScheduledOrderMinimumTimes(distArr)
          : [];
      setScheduledSegmentMinutes(segmentMinutes);

      if (formState.delivery_type === 'scheduled') {
        const syncedTimeState = buildScheduledRouteTimeState(segmentMinutes);
        if (syncedTimeState) {
          setPickup(syncedTimeState.pickup);
          setDrop(syncedTimeState.drop);
          setDroplocations(syncedTimeState.droplocations);
        }
      }

      let finalAmount = totalDistance <= 1.0 ? (priceManipulation?.base_order_charges || 0) : totalAmount + (priceManipulation?.base_order_charges || 0);

      const extraWeight = getWeightCharge(formState.parcel_weight);
      finalAmount += extraWeight;
      const flexibleDeliveryDiscountPercent =
        formState.delivery_type === 'flexible'
          ? Math.min(
              Math.max(
                Number(
                  priceManipulation?.flexibleDeliveryDiscountPercent ?? 0
                ) || 0,
                0
              ),
              100
            )
          : 0;
      const standardFareAmount = Math.round(finalAmount);
      const discountedFareAmount = Math.round(
        (finalAmount - extraWeight) * (1 - flexibleDeliveryDiscountPercent / 100) + extraWeight
      );
      const flexibleDeliveryDiscountAmount = Math.max(
        standardFareAmount - discountedFareAmount,
        0
      );
      // This is only the offline preview fallback. The authoritative quote
      // below always supplies the live pickup-zone rate before an edit is
      // saved.
      const heatmapPriceHikePercent = Math.max(
        Number(order?.heatmap_price_hike_percent) || 0,
        0
      );
      const heatmapAdjustedFareAmount =
        heatmapPriceHikePercent > 0
          ? Math.round(discountedFareAmount * heatmapPriceHikePercent)
          : discountedFareAmount;
      const heatmapPriceHikeAmount = Math.max(
        heatmapAdjustedFareAmount - discountedFareAmount,
        0
      );
      const securityFee = calculateSecurityFeeAmount(
        formState.parcel_value,
        priceManipulation?.security_fees_charges
      );
      const walletDueComponent = getWalletDueComponent(order);
      const recalculatedAmount =
        heatmapAdjustedFareAmount + securityFee + walletDueComponent;
      const nextAmount = mainDistance === 0 ? oldAmount : recalculatedAmount;
      const routeWithoutWeight =
        mainDistance === 0
          ? Math.max(
              Number(oldAmount || 0) - securityFee - walletDueComponent,
              0
            )
          : Math.max(
              Math.round(finalAmount - extraWeight) + heatmapPriceHikeAmount,
              0
            );

      const quoteNumber = (key, fallback = 0) =>
        quote ? Number(quote[key]) || 0 : fallback;
      const previewAmount = quoteNumber('amount', nextAmount);
      const previewFlexibleDiscount = quoteNumber(
        'flexibleDeliveryDiscountAmount',
        flexibleDeliveryDiscountAmount
      );
      const previewDeliveryCharge = quoteNumber(
        'deliveryCharge',
        heatmapAdjustedFareAmount
      );
      const previewSecurityFee = quoteNumber('securityFee', securityFee);
      const previewHeatmapAmount = quoteNumber(
        'heatmapPriceHikeAmount', heatmapPriceHikeAmount
      );
      const previewHeatmapPercent = quoteNumber(
        'heatmapPriceHikePercent', heatmapPriceHikePercent
      );

      setAmount(previewAmount);
      setPricingPreview({
        // deliveryCharge already includes the quoted heatmap surcharge.
        // Keep it in the displayed route amount; security stays separate.
        routeCharge: quote
          ? Math.max(
              previewDeliveryCharge +
                previewFlexibleDiscount -
                (mainDistance === 0 ? 0 : extraWeight),
              0
            )
          : routeWithoutWeight,
        weightCharge: mainDistance === 0 ? 0 : extraWeight,
        flexibleDiscount: quote
          ? previewFlexibleDiscount
          : mainDistance === 0
            ? Math.max(
                Number(order?.flexible_delivery_discount_amount) || 0,
                0
              )
            : flexibleDeliveryDiscountAmount,
        heatmapPriceHikeAmount: previewHeatmapAmount,
        heatmapPriceHikePercent: previewHeatmapPercent,
        securityFee: previewSecurityFee,
        walletDue: quoteNumber('walletDue', walletDueComponent),
        totalDistance,
        total: previewAmount,
      });
      return {
        amount: previewAmount,
        securityFee: previewSecurityFee,
        distances: quote?.distances || distArr,
        flexibleDeliveryDiscountPercent: quoteNumber(
          'flexibleDeliveryDiscountPercent',
          flexibleDeliveryDiscountPercent
        ),
        flexibleDeliveryDiscountAmount: previewFlexibleDiscount,
      };
    } catch (error) {
      console.error('Error fetching distance and cost:', error);
      if (showErrors) {
        toast.error('Failed to calculate fare. Please try again.');
      }
      return null;
    } finally {
      setPriceLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!canAutoUpdateFare) {
      return undefined;
    }

    const timer = setTimeout(() => {
      fetchDistanceAndCost({ showErrors: false });
    }, 600);

    return () => clearTimeout(timer);
  }, [
    canAutoUpdateFare,
    pickup.latitude,
    pickup.longitude,
    drop.latitude,
    drop.longitude,
    dropRouteSignature,
    formState.parcel_weight,
    formState.parcel_value,
  ]);

  const validateAddresses = () => {
    if (!pickup.latitude || !pickup.longitude || !drop.latitude || !drop.longitude) {
      toast.error('Please select valid pickup and drop addresses.');
      return false;
    }

    if (!validateDifferentPickupAndDrop(pickup, drop, toast)) {
      showDuplicateAddressFeedback();
      return false;
    }

    if (!validateUniqueLocations([pickup, drop, ...droplocations])) {
      showDuplicateAddressFeedback();
      return false;
    }

    return true;
  };

  const validateOrderForm = () => {
    const errors = {};
    const validateLocation = (location, prefix, label, index = null) => {
      const getFieldKey = (field) =>
        prefix === 'droppoint'
          ? getDropPointErrorKey(field, index)
          : `${prefix}_${field}`;

      const phoneDigits = getPhoneDigits(location?.phone_number);
      const name = String(location?.name || '').trim();
      const hasCoordinates = Boolean(location?.latitude && location?.longitude);

      if (!hasCoordinates) {
        errors[getFieldKey('search')] = `Please select a valid ${label} address`;
      }

      if (!name) {
        errors[getFieldKey('name')] = 'Please enter contact name';
      } else if (name.length < 2) {
        errors[getFieldKey('name')] = 'Please enter a valid name';
      }

      if (!phoneDigits) {
        errors[getFieldKey('phone_number')] = 'Please enter mobile number';
      } else if (!isValidPhoneNumber(location?.phone_number)) {
        errors[getFieldKey('phone_number')] =
          'Please enter a valid 10-digit mobile number';
      }

      if (formState.delivery_type === 'scheduled') {
        if (prefix === 'pickup') {
          if (!location?.date) {
            errors[getFieldKey('date')] = 'Please select pickup date';
          } else if (location.date < pickupDateMin || location.date > pickupDateMax) {
            errors[getFieldKey('date')] =
              'Pickup date must be within the next 4 days including today';
          }
        }

        const hasFromTime = Boolean(location?.fromtime);
        const hasToTime = Boolean(location?.totime);
        const requiredFromTime =
          prefix === 'pickup'
            ? ''
            : prefix === 'drop'
              ? getRequiredFromTimeForStop('drop')
              : getRequiredFromTimeForStop('droppoint', index);
        const minimumToTime =
          prefix === 'pickup'
            ? getMinimumToTimeForStop('pickup', null, location?.fromtime || '')
            : prefix === 'drop'
              ? getMinimumToTimeForStop('drop', null, location?.fromtime || '')
              : getMinimumToTimeForStop('droppoint', index, location?.fromtime || '');
        const minimumMinutes =
          prefix === 'pickup'
            ? PICKUP_SCHEDULE_MINUTES
            : prefix === 'drop'
              ? getSegmentMinimumMinutes(0)
              : getSegmentMinimumMinutes(index + 1);

        if (!hasFromTime) {
          errors[getFieldKey('fromtime')] = 'Please select from time';
        }

        if (!hasToTime) {
          errors[getFieldKey('totime')] = 'Please select to time';
        }

        if (
          hasFromTime &&
          requiredFromTime &&
          compareTimeValues(location?.fromtime, requiredFromTime) === -1
        ) {
          errors[getFieldKey('fromtime')] =
            prefix === 'pickup'
              ? "Can't select time before current time"
              : prefix === 'drop'
              ? "Can't select time before pickup time"
              : "Can't select time before previous drop time";
        }

        if (hasFromTime && hasToTime && compareTimeValues(location?.totime, location?.fromtime) !== 1) {
          errors[getFieldKey('totime')] = 'To time must be after from time';
        }

        if (
          hasFromTime &&
          hasToTime &&
          minimumToTime &&
          compareTimeValues(location?.totime, minimumToTime) === -1
        ) {
          errors[getFieldKey('totime')] =
            prefix === 'pickup'
              ? `To time must be at least ${PICKUP_SCHEDULE_MINUTES} minutes after from time`
              : getDropTimeErrorMessage(minimumMinutes);
        }
      }
    };

    validateLocation(pickup, 'pickup', 'pickup');
    validateLocation(drop, 'drop', 'drop');
    droplocations.forEach((location, index) => {
      validateLocation(location, 'droppoint', `drop point ${index + 2}`, index);
    });

    if (!validateDifferentPickupAndDrop(pickup, drop)) {
      showDuplicateAddressFeedback();
      errors.location_match = 'Pickup and drop location cannot be the same.';
    }

    if (!validateUniqueLocations([pickup, drop, ...droplocations])) {
      showDuplicateAddressFeedback();
      errors.location_match =
        'Same address cannot be used for multiple stops.';
    }

    if (!formState.phone_number) {
      errors.phone_number = 'Please enter mobile number';
    } else if (!isValidPhoneNumber(formState.phone_number)) {
      errors.phone_number = 'Please enter a valid 10-digit phone number';
    }

    if (!formState.package || formState.package.trim() === '') {
      errors.package = "Please specify what you're sending";
    }

    const parcelValue = Number(formState.parcel_value) || 0;
    if (parcelValue < 1 || parcelValue > 30000) {
      errors.parcel_value = 'Parcel value must be between ₹1 and ₹30000';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the form errors before proceeding');
    }
    return Object.keys(errors).length === 0;
  };

  const validateScheduledTimeWindows = async () => {
    if (formState.delivery_type !== 'scheduled') {
      return true;
    }

    const locationsToValidate = [
      { location: pickup, prefix: 'pickup', index: null },
      { location: drop, prefix: 'drop', index: null },
      ...droplocations.map((location, index) => ({
        location,
        prefix: 'droppoint',
        index,
      })),
    ];

    const nextErrors = {};
    const getFieldKey = (prefix, field, index = null) =>
      prefix === 'droppoint' ? getDropPointErrorKey(field, index) : `${prefix}_${field}`;

    for (const { location, prefix, index } of locationsToValidate) {
      const hasFromTime = Boolean(location?.fromtime);
      const hasToTime = Boolean(location?.totime);

      if (!hasFromTime || !hasToTime) {
        continue;
      }

      const requiredFromTime =
        prefix === 'pickup'
          ? ''
          : prefix === 'drop'
            ? getRequiredFromTimeForStop('drop')
            : getRequiredFromTimeForStop('droppoint', index);

      if (
        requiredFromTime &&
        compareTimeValues(location.fromtime, requiredFromTime) === -1
      ) {
        nextErrors[getFieldKey(prefix, 'fromtime', index)] =
          prefix === 'pickup'
            ? "Can't select time before current time"
            : prefix === 'drop'
            ? "Can't select time before pickup time"
            : "Can't select time before previous drop time";
      }

      const minimumToTime =
        prefix === 'pickup'
          ? getMinimumToTimeForStop('pickup', null, location.fromtime)
          : prefix === 'drop'
            ? getMinimumToTimeForStop('drop', null, location.fromtime)
            : getMinimumToTimeForStop('droppoint', index, location.fromtime);

      if (!minimumToTime) {
        nextErrors[getFieldKey(prefix, 'totime', index)] =
          'Selected from time leaves no valid time range in the same day';
        continue;
      }

      if (compareTimeValues(location.totime, minimumToTime) === -1) {
        nextErrors[getFieldKey(prefix, 'totime', index)] =
          prefix === 'pickup'
            ? `To time must be at least ${PICKUP_SCHEDULE_MINUTES} minutes after from time`
            : getDropTimeErrorMessage(
                prefix === 'drop'
                  ? getSegmentMinimumMinutes(0)
                  : getSegmentMinimumMinutes(index + 1)
              );
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors((current) => ({
        ...current,
        ...nextErrors,
      }));
      toast.error('Please correct the scheduled time windows.');
      return false;
    }

    return true;
  };

  const runPreSubmitScheduledValidation = async () => {
    const pricing = await fetchDistanceAndCost();
    if (!pricing) {
      toast.error('Unable to calculate fare for the selected route.');
      return null;
    }

    return validateScheduledTimeWindows() ? pricing : null;
  };

  const addToWallet = async (amount, token) => {
    if (!canContinueEdit()) return false;
    try {
      const response = await axios.post(
        `${apiUrl}/customer-transactions/add-to-wallet`,
        {
          amount: Math.round(amount),
          orderId: order._id,
          reason: "Order price reduction refund",
          refundSource: "order_edit",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        toast.success(response.data?.message || `₹${Math.round(amount)} added to your wallet successfully!`);
        dispatchWebsiteNotification({
          eventKey: `payment:${order._id}:refund:${Math.round(amount)}`,
          title: "Payment Refunded",
          body: `₹${Math.round(amount)} has been refunded to your wallet for order #${order.orderId || order._id}.`,
          tag: `refund-${order._id}`,
        });
        return true;
      } else {
        toast.error('Failed to add refund to wallet');
        return false;
      }
    } catch (error) {
      console.error('Error adding refund to wallet:', error);
      toast.error('Failed to process refund to wallet');
      return false;
    }
  };

  const processWalletPayment = async (amount, token) => {
    if (!canContinueEdit()) return false;
    try {
      const response = await axios.post(
        `${apiUrl}/customer-transactions/wallet-order-payment`,
        {
          amount: Math.round(amount),
          orderId: order._id,
          time_stamp: new Date().getTime(),
          isUpdate: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        toast.success('Payment successful from wallet');
        dispatchWebsiteNotification({
          eventKey: `payment:${order._id}:wallet-update:${Math.round(amount)}`,
          title: "Payment Successful",
          body: `Wallet payment was successful for order #${order.orderId || order._id}.`,
          tag: `wallet-payment-${order._id}`,
        });
        return true;
      } else {
        toast.error('Wallet payment failed');
        return false;
      }
    } catch (error) {
      console.error('Error processing wallet payment:', error);
      toast.error('Failed to process wallet payment');
      return false;
    }
  };

  const handlePayment = async () => {
    if (isUpdating || isPreparingUpdate) {
      return;
    }
    submissionRevision.current = null;
    if (!canContinueEdit()) return;
    setIsPreparingUpdate(true);
    const progressAvailable = await editProgress.refresh();
    setIsPreparingUpdate(false);
    if (!progressAvailable) {
      toast.error('Unable to check pickup status. Please try again.');
      return;
    }
    if (!canContinueEdit()) return;
    // An arrival during this read can change locked draft fields. Do not use
    // this render's old weight or fare; let the customer review the new form.
    if (editProgressPolicy(editProgress.current.current).parcelLocked &&
        (formState.parcel_weight !== order.parcel_weight || formState.package !== (order.package || ''))) {
      setShow(false);
      toast.info('Parcel details are locked because the rider arrived at pickup. Please review the order.');
      return;
    }
    submissionRevision.current = editProgress.revision.current;

    if (!hasOrderChanges()) {
      toast.info(noChangesMessage());
      setShow(false);
      return;
    }

    if (priceLoading) {
      toast.info('Please wait while we update the fare.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to your account!');
      return;
    }

    if (!validateAddresses()) {
      return;
    }

    if (!validateOrderForm()) {
      return;
    }

    setIsPreparingUpdate(true);
    let latestPricing;
    try {
      latestPricing = await runPreSubmitScheduledValidation();
    } finally {
      setIsPreparingUpdate(false);
    }
    if (!latestPricing || !canContinueEdit()) return;
    setIsUpdating(true);
    const headers = { Authorization: `Bearer ${token}` };
    const timestamp = new Date().getTime();
    const newAmount = Number(latestPricing.amount) || 0;
    const amountDifference = newAmount - oldAmount;

    if (amountDifference < 0) {
      if (order.payment_method === 'cod') {
        await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
        return;
      }
      const success = await addToWallet(Math.abs(amountDifference), token);
      if (!success) {
        setIsUpdating(false);
        return;
      }
      toast.info('Refund added to wallet');
      await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
      return;
    }

    if (amountDifference <= 0) {
      await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
      return;
    }

    if (order.payment_method === 'cod') {
      await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
      return;
    }

    if (payment === 'online') {
      const res = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!canContinueEdit()) return;
      if (!res) {
        toast.error('Failed to load Razorpay SDK');
        setIsUpdating(false);
        return;
      }

      try {
        const orderRes = await axios.post(
          `${apiUrl}/razorpay/create-order`,
          {
            amount: amountDifference,
            currency: 'INR',
            receipt: `order_update_${timestamp}`,
            purpose: 'order_update',
            source: 'website',
            orderId: order._id,
            publicOrderId: order.orderId,
          },
          { headers }
        );

        if (!canContinueEdit()) return;
        let paymentCompleted = false;
        const options = {
          key: orderRes.data.keyId,
          amount: orderRes.data.amount,
          currency: 'INR',
          name: 'Instaport Delivery',
          description: 'Order Update Payment',
          image: 'https://instaportdelivery.com/assets/logo/logo.png',
          order_id: orderRes.data.id,
          handler: async function (response) {
            paymentCompleted = true;
            try {
            const verification = await axios.post(`${apiUrl}/razorpay/verify`, {
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              purpose: 'order_update',
              orderId: order._id,
            }, { headers });
            if (!verification.data?.success ||
                verification.data.paymentIntentId !== orderRes.data.paymentIntentId) {
              throw new Error('Order edit payment verification failed');
            }
            await axios.post(
              `${apiUrl}/customer-transactions/manual-log`,
              {
                orderId: order._id,
                type: 'online',
                transactionId: response.razorpay_payment_id,
                amount: amountDifference,
                time_stamp: timestamp,
                isUpdate: true,
              },
              { headers }
            );
            dispatchWebsiteNotification({
              eventKey: `payment:${order._id}:online-update:${timestamp}`,
              title: "Payment Successful",
              body: `Online payment was successful for order #${order.orderId || order._id}.`,
              tag: `online-payment-${order._id}`,
            });
            await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
            } catch (error) {
              console.error('Error confirming update payment:', error);
              toast.error('Payment was successful, but the order could not be updated.');
              setIsUpdating(false);
            }
          },
          prefill: {
            name: pickup.name || '',
            contact: formState.phone_number.replace(/\D/g, '').slice(2),
          },
          notes: { address: 'Instaport Corporate Office' },
          theme: { color: '#69068a' },
          modal: {
            ondismiss: () => {
              if (!paymentCompleted) {
                setIsUpdating(false);
              }
            },
          },
        };
        const rzp1 = new window.Razorpay(options);
        rzp1.on('payment.failed', () => setIsUpdating(false));
        rzp1.open();
      } catch (error) {
        console.error('Error processing payment:', error);
        toast.error('Failed to initiate payment');
        setIsUpdating(false);
      }
      return;
    }

    if (payment === 'wallet') {
      const paymentSuccess = await processWalletPayment(amountDifference, token);
      if (paymentSuccess) {
        await updateOrder(headers, timestamp, newAmount, latestPricing.securityFee, latestPricing.distances, amountDifference);
      } else {
        setIsUpdating(false);
      }
      return;
    }

    toast.error('Please choose a payment method for the updated amount.');
    setIsUpdating(false);
  };

  const updateOrder = async (headers, timestamp, updatedAmount, securityFee, updatedDistances, amountDifference = 0) => {
    if (!canContinueEdit()) return;
    setIsUpdating(true);
    try {
      if (!validateDifferentPickupAndDrop(pickup, drop, toast)) {
        showDuplicateAddressFeedback();
        return;
      }

      if (!validateUniqueLocations([pickup, drop, ...droplocations])) {
        showDuplicateAddressFeedback();
        return;
      }

      const locations = sanitizeAllLocations();
      const gstDetailsPayload = getGstDetailsPayload();
      let syncedPaymentAddress = null;
      if (order?.payment_method === 'cod') {
        const paymentKey = paymentAddress?.key || '';
        if (paymentKey) {
          syncedPaymentAddress = locations.find((loc) => loc.key === paymentKey) || null;
        }
        if (!syncedPaymentAddress) {
          syncedPaymentAddress = locations[1] || locations[0] || null;
        }
      }

      const response = await axios.patch(
        `${apiUrl}/order/customer/`,
        {
          _id: order._id,
          parcel_weight: formState.parcel_weight,
          phone_number: formState.phone_number,
          package: formState.package,
          locations,
          amount: updatedAmount,
          payment_method: order.payment_method,
          distances: updatedDistances,
          security_fee: securityFee,
          security_fee_percent: Number(priceData?.security_fees_charges) || 0,
          payment_address: syncedPaymentAddress,
          gstDetails: gstDetailsPayload,
          hold: amountDifference > 0 ? amountDifference : 0,
          paymentVerified: amountDifference > 0,
          paymentRequired: amountDifference > 0,
        },
        { headers }
      );

      if (response.data.error) {
        console.error('Update order error:', response.data);
        toast.error(
          response.data.message === 'Please update something in the order'
            ? response.data.message
            : 'Failed to update order: ' + response.data.message
        );
      } else {
        set(ref(db, 'orders/' + response.data.order._id), response.data.order);
        toast.success('Order updated successfully');
        setShow(false);
        setShowNote(true);
      }
    } catch (error) {
      console.error('Error updating order:', error);
      const serverMessage = error?.response?.data?.message;
      toast.error(
        serverMessage === 'Please update something in the order'
          ? serverMessage
          : 'Failed to update order: ' + (serverMessage || error.message || 'Unknown error')
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChange = (e) => {
    const nextValue =
      e.target.id === 'parcel_value'
        ? e.target.value === ''
          ? ''
          : String(Math.max(0, Number(e.target.value) || 0))
        : e.target.value;

    setFormState({
      ...formState,
      [e.target.id]: nextValue,
    });

    if (formErrors[e.target.id]) {
      clearFieldErrors(e.target.id);
    }
  };

  const handleAddDeliveryPoint = () => {
    setDroplocations([
      ...droplocations,
      {
        ...addressInitialState,
        key: uuidv4(),
        date: formState.delivery_type === 'scheduled' ? pickup.date || pickupDateMin : '',
      },
    ]);
  };

  const handleRemove = (dropIndex) => {
    if (window.confirm('Are you sure you want to delete the drop point?')) {
      setDroplocations(droplocations.filter((_, index) => index !== dropIndex));
    }
  };

  if (editProgress.blocked) {
    return <><Navbar /><main role="alert" className="min-h-screen flex flex-col items-center justify-center gap-6 p-8 text-center">
      <h1 className="text-2xl font-semibold">Order editing unavailable</h1>
      <p>{editProgress.message}</p>
      <button type="button" className="rounded-xl bg-yellow-300 px-6 py-3" onClick={() => navigate(-1)}>Back to order</button>
    </main><Footer /></>;
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="h-screen w-screen flex items-center justify-center">
          <div className="text-xl">Loading order details...</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ServiceabilityModal
        open={showServiceabilityModal}
        onClose={() => setShowServiceabilityModal(false)}
      />
      <section id="main" className="h-auto w-screen Poppins flex flex-col bg-[#fafae0]">
        <Navbar />
      <div
        className={`hidden md:block fixed bottom-6 left-6 z-50 bg-white border-2 border-accentYellow rounded-xl shadow-lg p-4 w-64 lg:w-80 transform transition-all duration-300 ${
          shouldShowFareBox ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="flex items-center justify-between mb-2 gap-3">
          <h3 className="font-bold text-lg text-gray-800">Updated Fare</h3>
          <span className="text-sm font-medium text-gray-700">Order Amount</span>
        </div>
        <div className="text-2xl font-bold text-accentYellow mb-2">
          ₹{Number(displayedFareAmount).toFixed(0)}
        </div>
        <div className="text-sm text-gray-600 space-y-1">
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-800">Route + Weight</span>
              <span className="text-sm font-semibold text-gray-800">
                ₹{routeAndWeightAmount.toFixed(0)}
              </span>
            </div>
            {Number(pricingPreview.flexibleDiscount || 0) > 0 && (
              <div className="flex justify-between items-center mt-1 text-green-700">
                <span className="text-sm font-medium">
                  Flexible delivery discount ({flexibleDiscountPercent}%)
                </span>
                <span className="text-sm font-semibold">
                  - ₹{Number(pricingPreview.flexibleDiscount).toFixed(0)}
                </span>
              </div>
            )}
            {Number(pricingPreview.securityFee || 0) > 0 && (
              <div className="flex justify-between items-center mt-1">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium text-gray-800">Security Fee</span>
                  <SecurityFeeInfoButton iconClassName="text-gray-500 text-xs" />
                </div>
                <span className="text-sm font-semibold text-gray-800">
                  ₹{Number(pricingPreview.securityFee || 0).toFixed(0)}
                </span>
              </div>
            )}
            {Number(pricingPreview.walletDue || 0) > 0 && (
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm font-medium text-red-600">Wallet Due</span>
                <span className="text-sm font-semibold text-red-600">
                  ₹{Number(pricingPreview.walletDue || 0).toFixed(0)}
                </span>
              </div>
            )}
          </div>
          <p>
            <span className="font-medium">Original:</span> ₹{Number(oldAmount || 0).toFixed(0)}
          </p>
          {amountDifference !== 0 && (
            <p className={amountDifference > 0 ? 'text-yellow-600' : 'text-green-600'}>
              {amountDifference > 0
                ? `Increase: ₹${amountDifference.toFixed(0)}`
                : `Decrease: ₹${Math.abs(amountDifference).toFixed(0)}`}
            </p>
          )}
          <p>
            <span className="font-medium">Weight:</span> {formState.parcel_weight}
          </p>
          {Number(pricingPreview.totalDistance || 0) > 0 && (
            <p>
              <span className="font-medium">Distance:</span>{' '}
              {Number(pricingPreview.totalDistance || 0).toFixed(2)} km
            </p>
          )}
          {pickup.latitude && drop.latitude && (
            <>
              <p>
                <span className="font-medium">Pickup:</span>{' '}
                {pickup.address
                  ? `${pickup.address.substring(0, 30)}...`
                  : 'Not selected'}
              </p>
              <p>
                <span className="font-medium">Drop:</span>{' '}
                {drop.address ? `${drop.address.substring(0, 30)}...` : 'Not selected'}
              </p>
            </>
          )}
          {droplocations.length > 0 && (
            <p className="text-xs">+{droplocations.length} additional stops</p>
          )}
        </div>
      </div>

      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-accentYellow shadow-lg p-3 transform transition-all duration-300 ${
          shouldShowFareBox ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div
          className="flex items-center justify-between cursor-pointer gap-3"
          onClick={() => setShowPriceDetails(!showPriceDetails)}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-lg font-bold text-accentYellow">
              ₹{Number(displayedFareAmount).toFixed(0)}
            </div>
            <div className="text-xs text-gray-500">{fareStatusMessage}</div>
          </div>
          <div className="text-sm text-gray-600 whitespace-nowrap">
            {showPriceDetails ? 'Hide details' : 'Show details'}
          </div>
        </div>

        {showPriceDetails && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-sm text-gray-600 space-y-1">
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-800">Route + Weight</span>
                  <span className="text-sm font-semibold text-gray-800">
                    ₹{routeAndWeightAmount.toFixed(0)}
                  </span>
                </div>
                {Number(pricingPreview.flexibleDiscount || 0) > 0 && (
                  <div className="flex justify-between items-center mt-1 text-green-700">
                    <span className="text-sm font-medium">
                      Flexible delivery discount ({flexibleDiscountPercent}%)
                    </span>
                    <span className="text-sm font-semibold">
                      - ₹{Number(pricingPreview.flexibleDiscount).toFixed(0)}
                    </span>
                  </div>
                )}
                {Number(pricingPreview.securityFee || 0) > 0 && (
                  <div className="flex justify-between items-center mt-1">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-gray-800">Security Fee</span>
                      <SecurityFeeInfoButton iconClassName="text-gray-500 text-xs" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">
                      ₹{Number(pricingPreview.securityFee || 0).toFixed(0)}
                    </span>
                  </div>
                )}
                {Number(pricingPreview.walletDue || 0) > 0 && (
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm font-medium text-red-600">Wallet Due</span>
                    <span className="text-sm font-semibold text-red-600">
                      ₹{Number(pricingPreview.walletDue || 0).toFixed(0)}
                    </span>
                  </div>
                )}
              </div>
              <p>
                <span className="font-medium">Original:</span> ₹{Number(oldAmount || 0).toFixed(0)}
              </p>
              {amountDifference !== 0 && (
                <p className={amountDifference > 0 ? 'text-yellow-600' : 'text-green-600'}>
                  {amountDifference > 0
                    ? `Increase: ₹${amountDifference.toFixed(0)}`
                    : `Decrease: ₹${Math.abs(amountDifference).toFixed(0)}`}
                </p>
              )}
              <p>
                <span className="font-medium">Weight:</span> {formState.parcel_weight}
              </p>
              {Number(pricingPreview.totalDistance || 0) > 0 && (
                <p>
                  <span className="font-medium">Distance:</span>{' '}
                  {Number(pricingPreview.totalDistance || 0).toFixed(2)} km
                </p>
              )}
              {pickup.latitude && drop.latitude && (
                <>
                  <p>
                    <span className="font-medium">Pickup:</span>{' '}
                    {pickup.address
                      ? `${pickup.address.substring(0, 20)}...`
                      : 'Not selected'}
                  </p>
                  <p>
                    <span className="font-medium">Drop:</span>{' '}
                    {drop.address ? `${drop.address.substring(0, 20)}...` : 'Not selected'}
                  </p>
                </>
              )}
              {droplocations.length > 0 && (
                <p className="text-xs">+{droplocations.length} additional stops</p>
              )}
            </div>
          </div>
        )}
      </div>
      <form id='orderForm' onSubmit={(e) => {
        e.preventDefault();
        if (isPreparingUpdate || isUpdating) {
          return;
        }
        submissionRevision.current = null;
        if (!canContinueEdit()) return;
        submissionRevision.current = editProgress.revision.current;
        if (!hasOrderChanges()) {
          toast.info(noChangesMessage());
          return;
        }

        if (priceLoading) {
          toast.info('Please wait while we update the fare.');
          return;
        }

        setIsPreparingUpdate(true);
        (async () => {
          try {
            if (!validateAddresses()) {
              return;
            }

            if (!validateOrderForm()) {
              return;
            }

            const latestPricing = await runPreSubmitScheduledValidation();
            if (!latestPricing || !canContinueEdit()) {
              return;
            }

            if (Number(latestPricing.amount) === 0) {
              toast.error('Please enter valid addresses to calculate fare first');
              return;
            }

            setShow(true);
          } finally {
            setIsPreparingUpdate(false);
          }
        })();
      }} className='relative w-full px-3 pb-24 pt-[68px] sm:px-4 sm:pt-[76px] lg:px-6'>
        <div className='mx-auto w-full max-w-none space-y-5'>
          <section className='relative overflow-hidden rounded-[30px] border border-[#f3e3b2] bg-[linear-gradient(180deg,#fffdf7_0%,#fff9ed_100%)] px-6 py-7 shadow-[0_18px_70px_rgba(196,151,69,0.14)] sm:px-8 lg:px-12 lg:py-8'>
            <div className='absolute inset-0 bg-[radial-gradient(circle_at_15%_18%,rgba(255,227,138,0.30),rgba(255,255,255,0)_24%),radial-gradient(circle_at_86%_18%,rgba(255,205,77,0.16),rgba(255,255,255,0)_18%),linear-gradient(90deg,rgba(255,250,239,0.98)_0%,rgba(255,248,232,0.92)_44%,rgba(255,248,235,0.55)_72%,rgba(255,248,235,0.18)_100%)]' />
            <div className='absolute right-[7%] top-[18%] hidden text-[#efb317] lg:block'>
              <FiMapPin size={26} />
            </div>
            <div className='relative grid items-center gap-8 lg:grid-cols-[minmax(0,1.02fr)_minmax(540px,0.98fr)]'>
              <div className='max-w-[620px] py-4 text-left'>
                <h1 className='text-4xl font-black tracking-[-0.05em] text-[#0f172a] sm:text-5xl lg:text-[64px] lg:leading-[1.02]'>
                  Edit Order
                </h1>
                <p className='mt-4 max-w-[540px] text-lg leading-9 text-[#465467] sm:text-[22px]'>
                  Update addresses, package details, timing and vehicle selection with the same fast workflow as order creation.
                </p>
                <div className='mt-4 flex flex-wrap items-center gap-3 text-[#efb317]'>
                  <FiMapPin size={24} />
                  <span className='text-sm font-semibold uppercase tracking-[0.22em] text-[#d7a313]'>
                    Order #{order?.orderId || 'N/A'}
                  </span>
                  <span className='rounded-full border border-[#f3d57c] bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#7c5c00]'>
                    {order?.status || 'Pending'}
                  </span>
                </div>
                <div className='mt-5 grid gap-3 sm:grid-cols-3'>
                  <div className='rounded-2xl border border-[#f3e3b2] bg-white/80 px-4 py-3 shadow-sm'>
                    <p className='text-xs font-semibold uppercase tracking-[0.16em] text-[#9b7b11]'>Original Fare</p>
                    <p className='mt-1 text-lg font-bold text-[#111827]'>₹{Number(oldAmount || 0).toFixed(0)}</p>
                  </div>
                  <div className='rounded-2xl border border-[#f3e3b2] bg-white/80 px-4 py-3 shadow-sm'>
                    <p className='text-xs font-semibold uppercase tracking-[0.16em] text-[#9b7b11]'>Updated Fare</p>
                    <p className='mt-1 text-lg font-bold text-[#111827]'>₹{Number(displayedFareAmount || 0).toFixed(0)}</p>
                  </div>
                  <div className='rounded-2xl border border-[#f3e3b2] bg-white/80 px-4 py-3 shadow-sm'>
                    <p className='text-xs font-semibold uppercase tracking-[0.16em] text-[#9b7b11]'>Payment</p>
                    <p className='mt-1 text-lg font-bold uppercase text-[#111827]'>{order?.payment_method || 'COD'}</p>
                  </div>
                </div>
              </div>
              <div className='relative min-h-[318px] overflow-hidden lg:min-h-[336px]'>
                <div className='absolute inset-y-0 right-0 w-[100%] bg-[radial-gradient(circle_at_68%_50%,rgba(255,214,103,0.92)_0%,rgba(255,226,142,0.52)_24%,rgba(255,244,221,0.16)_56%,rgba(255,248,235,0)_78%)]' />
                <div className='absolute inset-y-0 left-0 w-[46%] bg-[linear-gradient(90deg,#fff9ed_0%,rgba(255,249,237,0.96)_36%,rgba(255,249,237,0.28)_74%,rgba(255,249,237,0)_100%)]' />
                <div
                  className='absolute inset-y-0 right-0 h-full w-[102%] bg-contain bg-right-bottom bg-no-repeat'
                  style={{
                    backgroundImage: "url('/assets/delivery_man_images/create_order_1.png')",
                    WebkitMaskImage:
                      'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 10%, rgba(0,0,0,0.9) 24%, rgba(0,0,0,1) 36%, rgba(0,0,0,1) 93%, rgba(0,0,0,0.76) 97%, rgba(0,0,0,0) 100%), linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.82) 14%, rgba(0,0,0,1) 26%, rgba(0,0,0,1) 84%, rgba(0,0,0,0.74) 94%, rgba(0,0,0,0) 100%), radial-gradient(circle at 72% 56%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 30%, rgba(0,0,0,0.75) 52%, rgba(0,0,0,0.22) 74%, rgba(0,0,0,0) 90%)',
                    maskImage:
                      'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 10%, rgba(0,0,0,0.9) 24%, rgba(0,0,0,1) 36%, rgba(0,0,0,1) 93%, rgba(0,0,0,0.76) 97%, rgba(0,0,0,0) 100%), linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.82) 14%, rgba(0,0,0,1) 26%, rgba(0,0,0,1) 84%, rgba(0,0,0,0.74) 94%, rgba(0,0,0,0) 100%), radial-gradient(circle at 72% 56%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 30%, rgba(0,0,0,0.75) 52%, rgba(0,0,0,0.22) 74%, rgba(0,0,0,0) 90%)',
                    WebkitMaskComposite: 'source-in',
                    maskComposite: 'intersect',
                  }}
                />
                <div className='pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,#fff9ed_0%,rgba(255,249,237,0)_18%,rgba(255,249,237,0)_82%,#fff9ed_100%),linear-gradient(180deg,#fff9ed_0%,rgba(255,249,237,0)_16%,rgba(255,249,237,0)_84%,#fff9ed_100%)]' />
              </div>
            </div>
          </section>

          <div className='space-y-4'>
            <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3 xl:items-start'>
              <div className={formState?.delivery_type === "now" ? 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9e7_0%,#fff2c9_100%)] px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]' : 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#edf0ea] bg-white px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]'}>
                <div className='text-2xl text-[#f3a700]'><FiZap /></div>
                <div className='flex-1 pt-4'>
                  <div className='text-[32px] font-bold tracking-[-0.03em] leading-tight text-[#111827]'>Deliver Now</div>
                  <div className='mt-1 text-[18px] font-semibold text-[#111827]'>from ₹{priceData?.base_order_charges || 0}</div>
                  <div className='mt-3 text-[17px] leading-8 text-gray-500'>Our nearest courier will pick up and deliver as soon as possible.</div>
                </div>
                <div className='flex flex-wrap gap-2 pt-4'>
                  {DELIVERY_TYPE_FEATURES.now.map((item) => (
                    <span key={item} className='rounded-full border border-[#efcf7a] bg-white/70 px-3 py-1.5 text-sm font-semibold text-[#6b7280]'>{item}</span>
                  ))}
                </div>
              </div>
              <div className={formState?.delivery_type === "flexible" ? 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9e7_0%,#fff2c9_100%)] px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]' : 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#edf0ea] bg-white px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]'}>
                <div className='text-2xl text-[#f3a700]'><LuClock3 /></div>
                <div className='flex-1 pt-4'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='text-[32px] font-bold tracking-[-0.03em] leading-tight text-[#111827]'>Flexible Delivery</div>
                    <span className='shrink-0 text-[24px] font-extrabold leading-tight text-[#16803c]'>{flexibleDiscountLabel}</span>
                  </div>
                  <div className='mt-1 text-[18px] font-semibold text-[#111827]'>from {"\u20B9"}{priceData?.base_order_charges || 0}</div>
                  <div className='mt-3 text-[17px] leading-8 text-gray-500'>Pickup starts in around 2.5 hours with same-day delivery in 4-5 hours.</div>
                </div>
                <div className='flex flex-wrap gap-2 pt-4'>
                  {DELIVERY_TYPE_FEATURES.flexible.map((item) => (
                    <span key={item} className='rounded-full border border-[#dfe4db] bg-[#f8faf7] px-3 py-1.5 text-sm font-semibold text-[#6b7280]'>{item}</span>
                  ))}
                </div>
              </div>
              <div className={formState?.delivery_type === "scheduled" ? 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9e7_0%,#fff2c9_100%)] px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]' : 'flex min-h-[246px] w-full flex-col justify-between rounded-[24px] border border-[#edf0ea] bg-white px-6 py-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)]'}>
                <div className='text-2xl text-[#f3a700]'><FiCalendar /></div>
                <div className='flex-1 pt-4'>
                  <div className='text-[32px] font-bold tracking-[-0.03em] leading-tight text-[#111827]'>Schedule</div>
                  <div className='mt-1 text-[18px] font-semibold text-[#111827]'>from ₹{priceData?.base_order_charges || 0}</div>
                  <div className='mt-3 text-[17px] leading-8 text-gray-500'>Choose a convenient time for pickup and delivery scheduling.</div>
                </div>
                <div className='flex flex-wrap gap-2 pt-4'>
                  {DELIVERY_TYPE_FEATURES.scheduled.map((item) => (
                    <span key={item} className='rounded-full border border-[#dfe4db] bg-[#f8faf7] px-3 py-1.5 text-sm font-semibold text-[#6b7280]'>{item}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)]'>
              <h1 className='text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Weight of parcel</h1>
              <div className='mt-5 overflow-x-auto'>
                <div className='flex w-full min-w-max gap-3 pb-1'>
                  {weight.map((w, index) => (
                    <button
                      key={index}
                      type='button'
                      disabled={isParcelDetailsLocked}
                      onClick={() => handleWeightChange(w)}
                      className={`${formState?.parcel_weight === w
                        ? "min-w-[150px] flex-1 rounded-[14px] border border-[#efb317] bg-[#ffcb23] px-5 py-3 font-semibold text-[#433305] shadow-[0_10px_24px_rgba(255,203,35,0.25)] duration-200"
                        : 'min-w-[150px] flex-1 rounded-[14px] border border-[#dfe4db] bg-white px-5 py-3 font-semibold text-[#344054] hover:border-[#efcf7a] duration-200'} ${isParcelDetailsLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className='grid gap-4 xl:grid-cols-2'>
            <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
              <h1 className='text-[28px] mb-1 font-bold tracking-[-0.03em] text-[#111827]'>Pickup Point</h1>
              <div className='w-full flex flex-col items-start'>
                <label htmlFor={"pickup"} className='pb-1 lg:text-base text-sm'>Pickup Address<span className="text-red-500">*</span></label>
                <div className='relative w-full'>
                  <NearbyPlacesAutocomplete
                    value={pickup.text}
                    onChange={(text) => {
                      setPickup((current) => resetLocationSearch(current, text));
                      clearFieldErrors("pickup_search");
                    }}
                    onSelect={handlePickupSelect}
                    onServiceabilityRejected={openServiceabilityModal}
                    onServiceabilityError={(error) => toast.error(error.message)}
                    debounceMs={300}
                    id="pickup"
                    name="edit-order-pickup-location-search"
                    required={true}
                    placeholder='Pick your pickup address'
                    containerClassName='relative w-full'
                    className={`min-h-[88px] w-full resize-none rounded-2xl border-2 bg-white px-4 py-4 pr-20 text-sm leading-6 outline-none sm:pr-24 sm:text-base ${formErrors.pickup_search ? 'border-red-500' : 'border-accentYellow'}`}
                    dropdownClassName='absolute autocomplete-dropdown-container w-full shadow-lg z-10 mt-1'
                    suggestionClassName='suggestion-item px-3 py-3 cursor-pointer hover:bg-gray-50'
                    activeSuggestionClassName='suggestion-item--active px-3 py-3 cursor-pointer hover:bg-gray-100'
                    loadingClassName='w-full px-3 py-3 bg-white rounded-lg'
                    multiline
                    rows={3}
                    autoComplete="new-password"
                    rightAdornment={
                      <div className="absolute right-3 top-1/2 flex -translate-y-1/2 flex-col items-center gap-1.5">
                        {pickup.text ? (
                          <button type="button" onClick={() => clearLocationSearch("pickup")} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800" title="Clear pickup address">
                            <IoIosClose className="text-2xl" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={handleGetCurrentLocation}
                          disabled={isFetchingLocation}
                          className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${isFetchingLocation ? "bg-gray-400 cursor-not-allowed text-white" : "bg-accentYellow text-[#111827] hover:bg-accentYellow/90"}`}
                          title="Use Current Location"
                        >
                          {isFetchingLocation ? (
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                            </svg>
                          ) : (
                            <MdMyLocation className="text-xl" />
                          )}
                        </button>
                      </div>
                    }
                  />
                </div>
                {formErrors.pickup_search && <p className="text-red-500 text-xs mt-1">{formErrors.pickup_search}</p>}
              </div>

              <Input
                required={false}
                onChange={(e) => { setPickup({ ...pickup, building_and_flat: e.target.value }) }}
                value={pickup.building_and_flat}
                label={"Building / Wing / Flat No."}
                type={"text"}
                placeholder={"Enter building, wing or flat number"}
                id={"building_and_flat"}
              />

              <div className={formState.delivery_type === "scheduled" ? 'grid lg:grid-cols-3 gap-4' : "hidden"}>
                <Input
                  required={formState.delivery_type === "scheduled"}
                  onChange={(e) => { handlePickupDateChange(e.target.value) }}
                  value={pickup.date}
                  label={<>Date<span className="text-red-500">*</span></>}
                  type={"date"}
                  placeholder={"Enter your Date"}
                  id={"date"}
                  min={pickupDateMin}
                  max={pickupDateMax}
                  error={formErrors.pickup_date}
                />
                <Input
                  required={formState.delivery_type === "scheduled"}
                  onChange={(e) => {
                  const fromtime = e.target.value;
                    const requiredFromTime = getRequiredFromTimeForStop("pickup");
                    const maximumFromTime = getMaximumFromTimeForStop("pickup");
                    if (requiredFromTime && compareTimeValues(fromtime, requiredFromTime) === -1) {
                      toast.error("Can't select time before current time.");
                      return;
                    }
                    if (maximumFromTime && compareTimeValues(fromtime, maximumFromTime) === 1) {
                      toast.error("Please select an earlier pickup time for the same day.");
                      return;
                    }

                    const minimumToTime = getMinimumToTimeForStop("pickup", null, fromtime);
                    if (!minimumToTime) {
                      toast.error("No valid pickup time window is available in the same day.");
                      return;
                    }

                    syncScheduledRouteTimesFromPickup(fromtime);
                    clearFieldErrors("pickup_fromtime", "pickup_totime");
                  }}
                  value={pickup.fromtime}
                  label={<>From Time<span className="text-red-500">*</span></>}
                  type={"time"}
                  placeholder={"Select start time"}
                  id={"fromtime"}
                  min={getPickupInputMinimum() || undefined}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  max={getMaximumFromTimeForStop("pickup") || undefined}
                  error={formErrors.pickup_fromtime}
                />
                <Input
                  required={formState.delivery_type === "scheduled"}
                  onChange={(e) => {
                    const nextToTime = e.target.value;
                    const minimumToTime = getMinimumToTimeForStop("pickup");
                    if (minimumToTime && compareTimeValues(nextToTime, minimumToTime) === -1) {
                      toast.error(`Pickup to time can't be before ${minimumToTime}.`);
                      return;
                    }

                    setPickup((current) => ({
                      ...current,
                      totime: nextToTime,
                    }));
                    clearFieldErrors("pickup_totime");
                  }}
                  value={pickup.totime}
                  label={<>To Time<span className="text-red-500">*</span></>}
                  type={"time"}
                  placeholder={"Select end time"}
                  id={"totime"}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  min={getMinimumToTimeForStop("pickup") || undefined}
                  max={"23:59"}
                  error={formErrors.pickup_totime}
                />
              </div>

              <div className='grid lg:grid-cols-2 gap-4'>
                {renderPrefixedPhoneInput({
                  value: pickup.phone_number,
                  onChange: (text) => {
                    setPickup({ ...pickup, phone_number: text });
                    updatePhoneFieldError("pickup_phone_number", text);
                  },
                  label: <>Mobile Number<span className="text-red-500">*</span></>,
                  id: "phone_number",
                  error: formErrors.pickup_phone_number,
                })}
                <Input
                  required={true}
                  onChange={(e) => {
                    setPickup({ ...pickup, name: e.target.value });
                    clearFieldErrors("pickup_name");
                  }}
                  value={pickup.name}
                  label={<>Name<span className="text-red-500">*</span></>}
                  type={"text"}
                  placeholder={"Enter your name"}
                  id={"name"}
                  error={formErrors.pickup_name}
                />
              </div>
              <Input
                onChange={(e) => { setPickup({ ...pickup, instructions: e.target.value }) }}
                value={pickup.instructions}
                label={"Instructions"}
                type={"text"}
                placeholder={"Special delivery instructions"}
                id={"instructions"}
              />
            </div>

            <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
              <h1 className='text-[28px] mb-1 font-bold tracking-[-0.03em] text-[#111827]'>Drop Point</h1>
              <div className='w-full flex flex-col items-start'>
                <label htmlFor={"drop"} className='pb-1 text-sm lg:text-base'>Drop Address<span className="text-red-500">*</span></label>
                <NearbyPlacesAutocomplete
                  value={drop.text}
                  onChange={(text) => {
                    setDrop((current) => resetLocationSearch(current, text));
                    clearFieldErrors("drop_search");
                  }}
                  onSelect={handleDropSelect}
                  onServiceabilityRejected={openServiceabilityModal}
                  onServiceabilityError={(error) => toast.error(error.message)}
                  debounceMs={300}
                  id="drop"
                  name="edit-order-drop-location-search"
                  required={true}
                  placeholder='Pick your drop address'
                  containerClassName='relative w-full'
                  className={`min-h-[88px] w-full resize-none rounded-2xl border-2 bg-white px-4 py-4 pr-14 text-sm leading-6 outline-none sm:pr-16 sm:text-base ${formErrors.drop_search ? 'border-red-500' : 'border-accentYellow'}`}
                  dropdownClassName='absolute autocomplete-dropdown-container w-full shadow-lg z-10 mt-1'
                  suggestionClassName='suggestion-item px-3 py-3 cursor-pointer hover:bg-gray-50'
                  activeSuggestionClassName='suggestion-item--active px-3 py-3 cursor-pointer hover:bg-gray-100'
                  loadingClassName='w-full px-3 py-3 bg-white rounded-lg'
                  multiline
                  rows={3}
                  autoComplete="new-password"
                  rightAdornment={
                    drop.text ? (
                      <button type="button" onClick={() => clearLocationSearch("drop")} className="absolute right-3 top-2 flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800" title="Clear drop address">
                        <IoIosClose className="text-2xl" />
                      </button>
                    ) : null
                  }
                />
                {formErrors.drop_search && <p className="text-red-500 text-xs mt-1">{formErrors.drop_search}</p>}
              </div>

              <Input
                required={false}
                onChange={(e) => { setDrop({ ...drop, building_and_flat: e.target.value }) }}
                value={drop.building_and_flat}
                label={"Building / Wing / Flat No."}
                type={"text"}
                placeholder={"Enter building, wing or flat number"}
                id={"drop-building-and-flat"}
              />

              <div className={formState.delivery_type === "scheduled" ? 'grid lg:grid-cols-3 gap-4' : "hidden"}>
                <Input
                  required={formState.delivery_type === "scheduled"}
                  onChange={() => {}}
                  value={drop.date}
                  label={<>Date<span className="text-red-500">*</span></>}
                  type={"date"}
                  placeholder={"Enter your Date"}
                  id={"drop-date"}
                  readOnly
                  disabled
                />
                <Input
                  required={false}
                  onChange={(e) => {
                    const fromtime = e.target.value;
                    const requiredFromTime = getRequiredFromTimeForStop("drop");
                    const maximumFromTime = getMaximumFromTimeForStop("drop");

                    if (requiredFromTime && compareTimeValues(fromtime, requiredFromTime) === -1) {
                      toast.error("Can't select time before pickup time.");
                      return;
                    }

                    if (maximumFromTime && compareTimeValues(fromtime, maximumFromTime) === 1) {
                      toast.error("Please select an earlier drop time for the same day.");
                      return;
                    }

                    const minimumToTime = getMinimumToTimeForStop("drop", null, fromtime);
                    if (!minimumToTime) {
                      toast.error("No valid drop time window is available in the same day.");
                      return;
                    }

                    setDrop((current) => ({
                      ...current,
                      fromtime,
                      time: fromtime,
                      totime:
                        !current.totime || compareTimeValues(current.totime, minimumToTime) === -1
                          ? minimumToTime
                          : current.totime,
                    }));
                    clearFieldErrors("drop_fromtime", "drop_totime");
                  }}
                  value={drop.fromtime}
                  label={<>From Time</>}
                  type={"time"}
                  placeholder={"Select start time"}
                  id={"drop-fromtime"}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  min={getRequiredFromTimeForStop("drop") || undefined}
                  max={getMaximumFromTimeForStop("drop") || undefined}
                  error={formErrors.drop_fromtime}
                />
                <Input
                  required={false}
                  onChange={(e) => {
                    const nextToTime = e.target.value;
                    const minimumToTime = getMinimumToTimeForStop("drop");
                    if (minimumToTime && compareTimeValues(nextToTime, minimumToTime) === -1) {
                      toast.error(getDropTimeErrorMessage(getSegmentMinimumMinutes(0)));
                      return;
                    }

                    setDrop({ ...drop, totime: nextToTime, time: drop.fromtime || nextToTime });
                    clearFieldErrors("drop_totime");
                  }}
                  value={drop.totime}
                  label={<>To Time</>}
                  type={"time"}
                  placeholder={"Select end time"}
                  id={"drop-totime"}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  min={getMinimumToTimeForStop("drop") || undefined}
                  max={"23:59"}
                  error={formErrors.drop_totime}
                />
              </div>

              <div className='grid lg:grid-cols-2 gap-4'>
                {renderPrefixedPhoneInput({
                  value: drop.phone_number,
                  onChange: (text) => {
                    setDrop({ ...drop, phone_number: text });
                    updatePhoneFieldError("drop_phone_number", text);
                  },
                  label: <>Mobile Number<span className="text-red-500">*</span></>,
                  id: "phone_number",
                  error: formErrors.drop_phone_number,
                })}
                <Input
                  required={true}
                  onChange={(e) => {
                    setDrop({ ...drop, name: e.target.value });
                    clearFieldErrors("drop_name");
                  }}
                  value={drop.name}
                  label={<>Name<span className="text-red-500">*</span></>}
                  type={"text"}
                  placeholder={"Enter your name"}
                  id={"name"}
                  error={formErrors.drop_name}
                />
              </div>
              <Input
                onChange={(e) => { setDrop({ ...drop, instructions: e.target.value }) }}
                value={drop.instructions}
                label={"Instructions"}
                type={"text"}
                placeholder={"Special delivery instructions"}
                id={"instructions"}
              />
            </div>
          </div>

          {droplocations?.map((droplocation, index) => (
            <div key={index} className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
              <div className='flex justify-between items-center mb-4'>
                <h1 className='text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Drop Point {index + 2}</h1>
                <span onClick={() => handleRemove(index)} className='flex justify-center items-center h-8 w-8 bg-red-500 text-white rounded-md cursor-pointer hover:bg-red-600 transition-colors' title="Remove drop point">
                  <FiTrash2 className='text-lg' />
                </span>
              </div>

              <div className='w-full flex flex-col items-start'>
                <label htmlFor={`droppoint-${index}`} className='pb-1 text-sm lg:text-base'>Drop Address<span className="text-red-500">*</span></label>
                <NearbyPlacesAutocomplete
                  value={droplocation.text}
                  onChange={(text) => {
                    setDroplocations((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? resetLocationSearch(item, text) : item
                      )
                    );
                    clearFieldErrors(getDropPointErrorKey("search", index));
                  }}
                  onSelect={(location) => handleDropLocationSelect(index, location)}
                  onServiceabilityRejected={openServiceabilityModal}
                  onServiceabilityError={(error) => toast.error(error.message)}
                  debounceMs={300}
                  id={`droppoint-${index}`}
                  name={`edit-order-drop-point-search-${index}`}
                  required={true}
                  placeholder='Pick your drop address'
                  containerClassName='relative w-full'
                  className={`min-h-[88px] w-full resize-none rounded-2xl border-2 bg-white px-4 py-4 pr-14 text-sm leading-6 outline-none sm:pr-16 sm:text-base ${formErrors[getDropPointErrorKey("search", index)] ? 'border-red-500' : 'border-accentYellow'}`}
                  dropdownClassName='absolute autocomplete-dropdown-container w-full shadow-lg z-10 mt-1'
                  suggestionClassName='suggestion-item px-3 py-3 cursor-pointer hover:bg-gray-50'
                  activeSuggestionClassName='suggestion-item--active px-3 py-3 cursor-pointer hover:bg-gray-100'
                  loadingClassName='w-full px-3 py-3 bg-white rounded-lg'
                  multiline
                  rows={3}
                  autoComplete="new-password"
                  rightAdornment={
                    droplocation.text ? (
                      <button type="button" onClick={() => clearLocationSearch("droppoint", index)} className="absolute right-3 top-2 flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800" title="Clear delivery point address">
                        <IoIosClose className="text-2xl" />
                      </button>
                    ) : null
                  }
                />
                {formErrors[getDropPointErrorKey("search", index)] && (
                  <p className="text-red-500 text-xs mt-1">{formErrors[getDropPointErrorKey("search", index)]}</p>
                )}
              </div>

              <Input
                required={false}
                onChange={(e) => handleDropLocationChange(index, 'building_and_flat', e.target.value)}
                value={droplocation?.building_and_flat}
                label={"Building / Wing / Flat No."}
                type={"text"}
                placeholder={"Enter building, wing or flat number"}
                id={`building_and_flat-${index}`}
              />

              <div className={formState.delivery_type === "scheduled" ? 'grid lg:grid-cols-3 gap-4' : "hidden"}>
                <Input
                  required={formState.delivery_type === "scheduled"}
                  onChange={() => {}}
                  value={droplocation?.date}
                  label={<>Date<span className="text-red-500">*</span></>}
                  type={"date"}
                  placeholder={"Enter your Date"}
                  id={`date-${index}`}
                  readOnly
                  disabled
                />
                <Input
                  required={false}
                  onChange={(e) => {
                    const fromtime = e.target.value;
                    const requiredFromTime = getRequiredFromTimeForStop("droppoint", index);
                    const maximumFromTime = getMaximumFromTimeForStop("droppoint", index);

                    if (requiredFromTime && compareTimeValues(fromtime, requiredFromTime) === -1) {
                      toast.error("Can't select time before previous drop time.");
                      return;
                    }

                    if (maximumFromTime && compareTimeValues(fromtime, maximumFromTime) === 1) {
                      toast.error("Please select an earlier drop time for the same day.");
                      return;
                    }

                    const minimumToTime = getMinimumToTimeForStop("droppoint", index, fromtime);
                    if (!minimumToTime) {
                      toast.error("No valid drop time window is available in the same day.");
                      return;
                    }

                    handleDropLocationChange(index, 'fromtime', fromtime);
                    handleDropLocationChange(index, 'time', fromtime);
                    handleDropLocationChange(
                      index,
                      'totime',
                      !droplocation?.totime || compareTimeValues(droplocation?.totime, minimumToTime) === -1
                        ? minimumToTime
                        : droplocation?.totime
                    );
                    clearFieldErrors(
                      getDropPointErrorKey("fromtime", index),
                      getDropPointErrorKey("totime", index)
                    );
                  }}
                  value={droplocation?.fromtime}
                  label={<>From Time</>}
                  type={"time"}
                  placeholder={"Select start time"}
                  id={`fromtime-${index}`}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  min={getRequiredFromTimeForStop("droppoint", index) || undefined}
                  max={getMaximumFromTimeForStop("droppoint", index) || undefined}
                  error={formErrors[getDropPointErrorKey("fromtime", index)]}
                />
                <Input
                  required={false}
                  onChange={(e) => {
                    const nextToTime = e.target.value;
                    const minimumToTime = getMinimumToTimeForStop("droppoint", index);
                    if (minimumToTime && compareTimeValues(nextToTime, minimumToTime) === -1) {
                      toast.error(
                        getDropTimeErrorMessage(getSegmentMinimumMinutes(index + 1))
                      );
                      return;
                    }

                    handleDropLocationChange(index, 'totime', nextToTime);
                    clearFieldErrors(getDropPointErrorKey("totime", index));
                  }}
                  value={droplocation?.totime}
                  label={<>To Time</>}
                  type={"time"}
                  placeholder={"Select end time"}
                  id={`totime-${index}`}
                  inputMode="none"
                  onKeyDown={preventManualTimeTyping}
                  onPaste={preventManualTimeTyping}
                  min={getMinimumToTimeForStop("droppoint", index) || undefined}
                  max={"23:59"}
                  error={formErrors[getDropPointErrorKey("totime", index)]}
                />
              </div>

              <div className='grid lg:grid-cols-2 gap-4'>
                {renderPrefixedPhoneInput({
                  value: droplocation?.phone_number,
                  onChange: (text) => {
                    handleDropLocationChange(index, 'phone_number', text);
                    updatePhoneFieldError(
                      getDropPointErrorKey("phone_number", index),
                      text
                    );
                  },
                  label: <>Mobile Number<span className="text-red-500">*</span></>,
                  id: `phone_number-${index}`,
                  error: formErrors[getDropPointErrorKey("phone_number", index)],
                })}
                <Input
                  required={true}
                  onChange={(e) => handleDropLocationChange(index, 'name', e.target.value)}
                  value={droplocation?.name}
                  label={<>Name<span className="text-red-500">*</span></>}
                  type={"text"}
                  placeholder={"Enter your name"}
                  id={`name-${index}`}
                  error={formErrors[getDropPointErrorKey("name", index)]}
                />
              </div>
              <Input
                onChange={(e) => handleDropLocationChange(index, 'instructions', e.target.value)}
                value={droplocation?.instructions}
                label={"Instructions"}
                type={"text"}
                placeholder={"Special delivery instructions"}
                id={`instructions-${index}`}
              />
            </div>
          ))}

          {formState.delivery_type !== 'flexible' && (
            <div onClick={handleAddDeliveryPoint} className='flex w-full cursor-pointer items-center justify-center rounded-[20px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9ea_0%,#fff5dd_100%)] px-6 py-4 text-center shadow-[0_10px_28px_rgba(255,201,52,0.14)] transition hover:shadow-[0_14px_34px_rgba(255,201,52,0.2)]'>
              <h1 className='flex items-center justify-center gap-3 text-xl font-bold text-[#1f2937]'><FiPlusCircle className='text-[22px] text-[#374151]' />Add a Delivery Point</h1>
            </div>
          )}

          <div className='grid gap-4 xl:grid-cols-2'>
            <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
              <h1 className='mb-1 flex items-center gap-3 text-[28px] font-bold tracking-[-0.03em] text-[#111827]'><FiBox className='text-[#d5a21b]' />What Commodity are you Sending?</h1>
              <Input
                required={true}
                onChange={handleChange}
                value={formState.package}
                type={"text"}
                placeholder={"E.g., Cake, Fruits, Clothes, Documents, Flowers, Vegetables, etc."}
                id={"package"}
                label={<>Package Type<span className="text-red-500">*</span></>}
                disabled={isParcelDetailsLocked}
                error={formErrors.package}
              />
              <div className='mt-4 flex flex-wrap gap-3'>
                {QUICK_PACKAGE_OPTIONS.map((packageType) => {
                  const isSelected =
                    formState.package.trim().toLowerCase() === packageType.toLowerCase();

                  return (
                    <button
                      key={packageType}
                      type='button'
                      disabled={isParcelDetailsLocked}
                      onClick={() => handleQuickPackageSelect(packageType)}
                      className={`${isSelected
                        ? 'rounded-full border border-[#efb317] bg-[#ffcb23] px-4 py-2.5 text-sm font-semibold text-[#433305] shadow-[0_10px_24px_rgba(255,203,35,0.2)] transition'
                        : 'rounded-full border border-[#dfe4db] bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] transition hover:border-[#efcf7a] hover:bg-[#fff9ea]'} ${isParcelDetailsLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      {packageType}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
              <h1 className='mb-1 flex items-center gap-3 text-[28px] font-bold tracking-[-0.03em] text-[#111827]'><FiShield className='text-[#3f7d2d]' />Insurance of Parcel</h1>
              <div className='flex flex-col w-full gap-3 items-end'>
                <Input
                  required={true}
                  onChange={handleChange}
                  value={formState.parcel_value}
                  label={<>Parcel Value (₹)<span className="text-red-500">*</span></>}
                  type={"number"}
                  placeholder={`Enter parcel value (₹1 - ₹30000)`}
                  id={"parcel_value"}
                  min={1}
                  max={30000}
                  disabled
                  error={formErrors.parcel_value}
                />
                <p className='w-full text-sm leading-6 text-gray-500'>* Insurance: 1% of declared value (up to ₹30,000 coverage)</p>
              </div>
              <Input
                required={true}
                onChange={(e) => {
                  let text = formatPhoneNumber(e.target.value);
                  setFormState({
                    ...formState,
                    phone_number: text
                  });
                  updatePhoneFieldError("phone_number", text);
                }}
                value={formState.phone_number}
                label={<>Mobile Number<span className="text-red-500">*</span></>}
                type={"text"}
                placeholder={"Enter phone number"}
                id={"phone_number"}
                error={formErrors.phone_number}
              />
              <p className='text-sm leading-6 text-gray-500'>Secure any delicate or crucial packages so that you may recover the value in the event that they are lost or damaged during delivery.</p>
            </div>
          </div>

          <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
            <h1 className='mb-1 text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Select Vehicle Type</h1>
            <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
              <div aria-disabled="true" className={formState.vehicle === "scooty" ? 'cursor-not-allowed rounded-[22px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9e7_0%,#fff2c9_100%)] p-5' : 'cursor-not-allowed rounded-[22px] border border-[#edf0ea] bg-white p-5'}>
                <div className='flex items-center justify-between gap-4'>
                  <div className='flex items-center gap-4'>
                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-[#ffcb23] text-[#fff] shadow-[0_8px_18px_rgba(255,203,35,0.28)]'>
                      <MdElectricScooter className='h-8 w-8' aria-hidden='true' />
                    </div>
                    <div>
                      <h3 className='text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Scooter</h3>
                      <p className='text-base text-gray-600'>For high capacity delivery</p>
                    </div>
                  </div>
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border ${formState.vehicle === "scooty" ? 'border-[#efb317] bg-[#ffcb23] text-white' : 'border-[#d6dbe1] bg-white text-transparent'}`}>
                    <FiCheck className='text-sm' />
                  </div>
                </div>
              </div>
              <div aria-disabled="true" className={formState.vehicle === "bike" ? 'cursor-not-allowed rounded-[22px] border border-[#efb317] bg-[linear-gradient(180deg,#fff9e7_0%,#fff2c9_100%)] p-5' : 'cursor-not-allowed rounded-[22px] border border-[#edf0ea] bg-white p-5'}>
                <div className='flex items-center justify-between gap-4'>
                  <div className='flex items-center gap-4'>
                    <div className='flex h-16 w-16 items-center justify-center rounded-full bg-[#285f1e] text-[#fff] shadow-[0_8px_18px_rgba(40,95,30,0.22)]'>
                      <MdTwoWheeler className='h-8 w-8' aria-hidden='true' />
                    </div>
                    <div>
                      <h3 className='text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Bike</h3>
                      <p className='text-base text-gray-600'>For low capacity delivery</p>
                    </div>
                  </div>
                  <div className={`flex h-7 w-7 items-center justify-center rounded-full border ${formState.vehicle === "bike" ? 'border-[#efb317] bg-[#ffcb23] text-white' : 'border-[#d6dbe1] bg-white text-transparent'}`}>
                    <FiCheck className='text-sm' />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='rounded-[24px] border border-[#edf0ea] bg-white px-5 py-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:px-6 sm:py-6'>
            <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
              <h1 className='text-[28px] font-bold tracking-[-0.03em] text-[#111827]'>Live Fare Preview</h1>
              <p className='text-sm text-gray-500'>{fareStatusMessage}</p>
            </div>
            <div className='mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4'>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'><span>Order Amount</span><span>₹{orderAmount.toFixed(2)}</span></div>
                <div className='flex justify-between'><span>Weight Charge</span><span>₹{Number(pricingPreview.weightCharge || 0).toFixed(2)}</span></div>
                {Number(pricingPreview.flexibleDiscount || 0) > 0 && (
                  <div className='flex justify-between text-green-700'>
                    <span>Flexible delivery discount ({flexibleDiscountPercent}%)</span>
                    <span>- ₹{Number(pricingPreview.flexibleDiscount).toFixed(2)}</span>
                  </div>
                )}
                {securityFeeAmount > 0 && (
                  <div className='flex justify-between text-red-600'>
                    <div className='flex items-center gap-1'>
                      <span>Security Fee</span>
                      <SecurityFeeInfoButton iconClassName="text-gray-500 text-xs" />
                    </div>
                    <span>₹{securityFeeAmount.toFixed(2)}</span>
                  </div>
                )}
                {Number(pricingPreview.walletDue || 0) > 0 && (
                  <div className='flex justify-between text-red-600'><span>Wallet Due</span><span>₹{Number(pricingPreview.walletDue || 0).toFixed(2)}</span></div>
                )}
                {Number(pricingPreview.totalDistance || 0) > 0 && (
                  <div className='flex justify-between text-gray-600'><span>Total Distance</span><span>{Number(pricingPreview.totalDistance || 0).toFixed(2)} km</span></div>
                )}
                <div className='flex justify-between border-t border-gray-300 pt-1 font-semibold'>
                  <span>Total</span>
                  <span>₹{Number(displayedFareAmount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
            {amountDifference !== 0 && (
              <p className={`mt-3 text-sm ${amountDifference > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                {amountDifference > 0
                  ? `Price increased by ₹${amountDifference.toFixed(2)}`
                  : `Price decreased by ₹${Math.abs(amountDifference).toFixed(2)}`}
              </p>
            )}
          </div>

          <div className='flex justify-center py-3'>
            <button
              type='submit'
              disabled={isUpdating || isPreparingUpdate}
              className='flex min-w-[320px] items-center justify-center gap-4 rounded-[16px] bg-[linear-gradient(180deg,#315f1f_0%,#214d14_100%)] px-10 py-4 text-center text-[31px] font-bold tracking-[-0.03em] text-white shadow-[0_16px_34px_rgba(36,86,24,0.22)] transition hover:translate-y-[-1px] hover:shadow-[0_20px_40px_rgba(36,86,24,0.28)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0'
            >
              {isPreparingUpdate ? "Checking..." : isUpdating ? "Updating..." : amount > 0 ? `Update Order - ₹${amount.toFixed(0)}` : "Update Order"}
              <span className='flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#29571c]'>
                {isUpdating || isPreparingUpdate ? <span className='h-4 w-4 animate-spin rounded-full border-2 border-[#29571c] border-t-transparent' /> : <FiArrowRight className='text-lg' />}
              </span>
            </button>
          </div>

          <div className='space-y-4'>
            <div className='rounded-[24px] border border-[#eef0e7] bg-[linear-gradient(180deg,#fbfdf6_0%,#f6faed_100%)] px-6 py-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]'>
              <div className='flex items-start gap-4'>
                <div className='flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#eef8e9] text-[#2d6a1d]'>
                  <FiShield className='text-[26px]' />
                </div>
                <div className='text-left'>
                  <p className='text-[15px] leading-7 text-[#475467]'>By clicking "Update Order", you confirm that the edited information is accurate and you agree to the updated fare, timing, and delivery details.</p>
                </div>
              </div>
            </div>

            <div className='rounded-[24px] border border-[#eef0e7] bg-white px-6 py-8 shadow-[0_16px_40px_rgba(15,23,42,0.06)]'>
              <h2 className='text-center text-[38px] font-bold tracking-[-0.04em] text-[#24561a]'>Simple Steps to Update</h2>
              <div className='mt-8 grid gap-4 md:grid-cols-5'>
                {[
                  { icon: <FiMapPin />, text: '1. Review pickup and delivery details' },
                  { icon: <FiCalendar />, text: '2. Adjust schedule if needed' },
                  { icon: <FiCheckCircle />, text: '3. Review updated fare instantly' },
                  { icon: <FiPhone />, text: '4. Confirm the changes and payment impact' },
                  { icon: <FiTruck />, text: '5. Track the updated order normally' },
                ].map((step, index) => (
                  <div key={index} className='relative flex flex-col items-center text-center'>
                    {index !== 4 && <div className='absolute left-[64%] top-8 hidden h-px w-[72%] border-t border-dashed border-[#d4d9ce] md:block' />}
                    <div className='flex h-16 w-16 items-center justify-center rounded-full border border-[#f2d68c] bg-[#fffdf7] text-[27px] text-[#24561a] shadow-[0_8px_22px_rgba(245,196,76,0.12)]'>
                      {step.icon}
                    </div>
                    <p className='mt-4 text-sm leading-6 text-[#475467]'>{step.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

        {/* Confirmation Modal */}
        <section
          className={
            show
              ? 'flex justify-center items-center z-[5000] fixed top-0 left-0 bg-black/40 h-screen w-screen opacity-100 duration-200 pointer-events-auto'
              : 'z-[5000] fixed top-0 left-0 bg-black/40 h-screen w-screen opacity-0 duration-200 pointer-events-none flex justify-center items-center'
          }
        >
          <div className="border-accentYellow border-2 bg-white lg:w-[40%] w-full h-auto flex flex-col gap-6 my-4 lg:py-8 py-4 lg:px-10 px-4 rounded-2xl">
            <div className="flex justify-between items-center">
              <h1 className="text-xl text-left w-full font-bold">Update Order</h1>
              <span
                className="cursor-pointer bg-gray-50 hover:bg-gray-100 duration-200 h-8 w-8 flex justify-center items-center rounded-md"
                onClick={() => setShow(false)}
              >
                <IoIosClose className="text-3xl" />
              </span>
            </div>
            <div>
              <Input value={amount.toFixed(2)} label="Amount" onChange={() => {}} />
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mt-2">
                <p className="font-semibold mb-1 text-sm">Breakdown</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span>Route + Weight</span><span>₹{Number((pricingPreview.routeCharge || 0) + (pricingPreview.weightCharge || 0)).toFixed(2)}</span></div>
                  {Number(pricingPreview.flexibleDiscount || 0) > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Flexible delivery discount ({flexibleDiscountPercent}%)</span>
                      <span>- ₹{Number(pricingPreview.flexibleDiscount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-red-600">
                    <div className="flex items-center gap-1">
                      <span>Security Fee</span>
                      <SecurityFeeInfoButton iconClassName="text-gray-500 text-xs" />
                    </div>
                    <span>₹{Number(pricingPreview.securityFee || 0).toFixed(2)}</span>
                  </div>
                  {Number(pricingPreview.walletDue || 0) > 0 && (
                    <div className="flex justify-between text-red-600"><span>Wallet Due</span><span>₹{Number(pricingPreview.walletDue || 0).toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between border-t border-gray-300 pt-1 font-semibold">
                    <span>Total</span>
                    <span>₹{Number(pricingPreview.total ?? amount ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              {amountDifference !== 0 && (
                <p className={`text-sm mt-1 ${amountDifference > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {amountDifference > 0
                    ? `Price increased by ₹${amountDifference.toFixed(2)}`
                    : `Price decreased by ₹${Math.abs(amountDifference).toFixed(2)}`}
                </p>
              )}
            </div>
            {amount > oldAmount && order?.payment_method !== 'cod' && (
              <div className="w-[60%] m-auto flex justify-center gap-6">
                <button
                  type='button'
                  onClick={() => setPayment('online')}
                  className={payment === 'online' ? 'outline-none rounded-xl px-7 py-2 bg-accentYellow border-accentYellow border-2 hover:shadow-md duration-200 ' : 'outline-none rounded-xl px-7 py-2 border-accentYellow border-2 hover:shadow-md duration-200 '}
                >
                  Pay Online
                </button>
                <button
                  type='button'
                  onClick={() => setPayment('wallet')}
                  className={payment === 'wallet' ? 'outline-none rounded-xl px-7 py-2 bg-accentYellow border-accentYellow border-2 hover:shadow-md duration-200 ' : 'outline-none rounded-xl px-7 py-2 border-accentYellow border-2 hover:shadow-md duration-200 '}
                >
                  Pay from Wallet
                </button>
              </div>
            )}
            <div className="flex justify-center items-center">
              <Button
                type="button"
                onClick={handlePayment}
                disabled={isUpdating || isPreparingUpdate}
                text={isUpdating || isPreparingUpdate ? <><span>{isPreparingUpdate ? 'Checking...' : 'Updating...'}</span><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /></> : "Update Order"}
                className="mx-auto flex items-center justify-center gap-2"
              />
            </div>
          </div>
        </section>
        </div>
      </form>
      <div className='mt-8 overflow-hidden rounded-t-[32px] bg-white shadow-[0_-10px_30px_rgba(177,146,90,0.05)]'>
        <Footer />
      </div>

      <div className={showNote ? 'fixed top-0 left-0 h-screen w-screen flex items-center justify-center z-[60] bg-black/40 opacity-100 duration-100 pointer-events-auto' : 'duration-100 pointer-events-none opacity-0 fixed top-0 left-0 h-screen w-screen flex items-center justify-center z-[60] bg-black/40'}>
        <div className='p-4 rounded-lg bg-white lg:w-[40vw] w-[80vw]'>
          <div className='flex justify-between items-center'>
            <h1 className='text-xl text-left w-full font-bold'>Note</h1>
            <span className='cursor-pointer bg-gray-50 hover:bg-gray-100 duration-200 h-8 w-8 flex justify-center items-center rounded-md' onClick={() => {
              setShowNote(false);
              navigate('/orders');
            }}><IoIosClose className='text-3xl' /></span>
          </div>
          <div className='py-2 flex flex-col'>
            <h2>Your order has been updated successfully!</h2>
            <p className='mt-2'>For better experience download our app.</p>
            <div className='flex items-center mt-3 gap-4'>
              <img src="/assets/footer/appstore.png" alt="" />
              <img src="/assets/footer/playstore.png" alt="" />
            </div>
          </div>
        </div>
      </div>

      </section>
    </>
  )
}

export default EditOrder
